<template>
  <div>
    <Breadcrumbs :items="breadcrumbs" page_title="live" />

    <v-container fluid class="host-live-page pa-3 pa-md-4">
      <v-alert
        v-if="errorMessage"
        type="error"
        dense
        outlined
        class="mb-4"
        data-testid="host-alert-error"
      >
        {{ errorMessage }}
      </v-alert>

      <v-alert
        v-if="infoMessage"
        type="info"
        dense
        text
        class="mb-4"
        data-testid="host-alert-info"
      >
        {{ infoMessage }}
      </v-alert>

      <div class="host-header mb-4 mb-md-5">
        <div class="host-header__title">{{ headerTitle }}</div>
        <div class="host-header__subtitle">{{ headerSubtitle }}</div>

        <div class="host-header__actions mt-3">
          <v-chip class="host-header__badge" :color="statusBadgeColor" text-color="white" small label>
            <v-icon left x-small color="white">mdi-record-circle</v-icon>
            {{ statusBadgeLabel }}
          </v-chip>

          <v-btn
            v-if="check_Permissions('live')"
            outlined
            color="#64748b"
            small
            class="host-header__button"
            :loading="isRecordingLoading"
            :disabled="isLoading || isRecordingLoading || isRecordingCooldown"
            data-testid="host-record-btn"
            @click="handleToggleRecording"
          >
            <v-icon left small :color="isRecording ? '#dc2626' : '#16a34a'">
              {{ isRecording ? 'mdi-pause-circle' : 'mdi-record-circle' }}
            </v-icon>
            {{ recordingButtonLabel }}
          </v-btn>

          <!-- B03 (F2/D24) — สถานะการอัด / auto-record ข้างปุ่ม (ไม่ใช้ infoMessage — E57) -->
          <div v-if="isRecording" class="host-header__rec-status" data-testid="host-rec-indicator">
            <span class="host-header__rec-dot"></span>
            Recording in progress
          </div>
          <div
            v-else-if="autoRecordWatchState !== 'idle'"
            class="host-header__rec-status host-header__auto-record"
            :class="'host-header__auto-record--' + autoRecordWatchState"
            :data-state="autoRecordWatchState"
            data-testid="host-auto-record-status"
            role="status"
            aria-live="polite"
          >
            <v-progress-circular
              v-if="autoRecordWatchState === 'starting'"
              indeterminate
              size="14"
              width="2"
              color="#64748b"
            />
            <v-icon v-else x-small color="#92400e">mdi-alert-outline</v-icon>
            <span>{{ autoRecordWatchState === 'starting' ? $t('livefeature_auto_record_starting') : autoRecordNotStartedText }}</span>
          </div>

          <v-btn
            v-if="check_Permissions('live')"
            color="#dc2626"
            dark
            small
            class="host-header__button host-header__button--danger"
            :disabled="isLoading || isEndingLive"
            data-testid="host-end-btn"
            @click="showEndLiveDialog = true"
          >
            <v-icon left small color="white">mdi-phone-hangup</v-icon>
            {{ isEndingLive ? $t('livefeature_ending') : 'End' }}
          </v-btn>
        </div>
      </div>

      <v-row class="ma-0 host-workspace" align="stretch">
        <v-col cols="12" md="9" class="pa-2 host-workspace__media">
          <div class="stage-column">
            <div class="stage-viewport">
              <div class="stage-canvas" :class="{ 'stage-canvas--grid': !controlState.share }">
                <!-- Full-bleed screen share feed (fills the whole canvas like Google Meet) -->
                <video
                  v-show="controlState.share"
                  ref="screenShareEl"
                  autoplay
                  muted
                  playsinline
                  class="stage-screenshare"
                ></video>

                <div class="stage-canvas__watching">
                  <v-icon left x-small color="#475569">mdi-eye</v-icon>
                  {{ watchingLabel }}
                </div>

                <!-- Recording indicator badge on canvas -->
                <div v-if="isRecording" class="stage-canvas__rec-badge" data-testid="host-stage-rec-badge">
                  <span class="stage-canvas__rec-dot"></span>
                  REC
                </div>

                <div
                  v-if="remoteVideoTracks.length === 0 && !controlState.camera"
                  class="stage-canvas__content"
                >
                  <!-- Placeholder shown when not sharing and no streams are active -->
                  <div v-show="!controlState.share" class="stage-canvas__center">
                    <div class="stage-canvas__icon-wrap">
                      <v-icon size="72" color="#16a34a">{{ stageIcon }}</v-icon>
                    </div>
                    <div class="stage-canvas__headline">
                      {{ stageTitle }}
                    </div>
                    <div class="stage-canvas__subline">
                      {{ stageSubtitle }}
                    </div>
                  </div>
                </div>

                <div class="stage-canvas__timer">
                  <v-icon left x-small color="white">mdi-timer-outline</v-icon>
                  {{ sessionTimerLabel }}
                </div>

                <!-- Drawing overlay canvas -->
                <canvas
                  ref="drawCanvas"
                  class="stage-draw-canvas"
                  :class="{ 'stage-draw-canvas--active': controlState.draw }"
                  @mousedown="onDrawStart"
                  @mousemove="onDrawMove"
                  @mouseup="onDrawEnd"
                  @mouseleave="onDrawEnd"
                ></canvas>

                <!-- Embedded video + draw overlay wrapper -->
                <div v-if="embedActive" class="stage-embed-wrap">
                  <iframe
                    ref="embedFrame"
                    class="stage-embed-frame"
                    :src="embedUrl"
                    frameborder="0"
                    allowfullscreen
                    allow="autoplay; encrypted-media"
                  ></iframe>
                  <canvas
                    ref="embedDrawCanvas"
                    class="stage-draw-canvas stage-embed-draw"
                    :class="{ 'stage-draw-canvas--active': controlState.draw }"
                    @mousedown="onDrawStart"
                    @mousemove="onDrawMove"
                    @mouseup="onDrawEnd"
                    @mouseleave="onDrawEnd"
                  ></canvas>
                </div>

                <!-- Conference grid — always in DOM; CSS toggles between grid and filmstrip -->
                <div
                  class="conf-grid"
                  :class="{ 'conf-grid--filmstrip': controlState.share }"
                  :style="gridStyleVars"
                >
                  <!-- Host tile (always first) -->
                  <div class="conf-tile conf-tile--self">
                    <video
                      ref="localVideoEl"
                      v-show="controlState.camera"
                      class="conf-tile__video"
                      autoplay
                      muted
                      playsinline
                    ></video>
                    <div v-show="!controlState.camera" class="conf-tile__avatar">
                      <v-avatar size="72" color="#dbeafe">
                        <span class="conf-tile__initials">{{ currentAdminInitials }}</span>
                      </v-avatar>
                    </div>
                    <div class="conf-tile__bar">
                      <span class="conf-tile__name">{{ currentAdminPreviewLabel }}</span>
                      <v-icon size="14" :color="controlState.mic ? '#22c55e' : '#ef4444'">
                        {{ controlState.mic ? 'mdi-microphone' : 'mdi-microphone-off' }}
                      </v-icon>
                    </div>
                  </div>

                  <!-- Remote participant tiles -->
                  <div
                    v-for="rp in remoteVideoTracks"
                    :key="rp.sid"
                    class="conf-tile"
                  >
                    <video
                      :ref="'rv_' + rp.sid"
                      class="conf-tile__video"
                      autoplay
                      playsinline
                    ></video>
                    <div class="conf-tile__bar">
                      <span class="conf-tile__name">{{ rp.name }}</span>
                    </div>
                  </div>
                </div>

                <div ref="remoteAudioSink" class="remote-audio-sink" aria-hidden="true"></div>
              </div>
            </div>

            <div class="stage-dock stage-dock--between">
              <button
                v-for="control in controls"
                :key="control.label"
                type="button"
                class="stage-dock__action"
                :disabled="control.action === 'mute-all' && muteAllLoading"
                :class="{
                  'stage-dock__action--active': isControlActive(control),
                  'stage-dock__action--poll': control.action === 'create-poll'
                }"
                @click="handleControlClick(control)"
              >
                <v-icon
                  size="20"
                  :class="{ 'stage-dock__icon--loading': control.action === 'mute-all' && muteAllLoading }"
                >
                  {{ control.action === 'mute-all' && muteAllLoading ? 'mdi-loading' : control.icon }}
                </v-icon>
                <span>{{ control.action === 'mute-all' && muteAllLoading ? 'Muting...' : control.label }}</span>
              </button>
            </div>

            <!-- B01: แผงผลโหวตใช้ scoped CSS .poll-panel* เดิม + ช่องรูปตัวเลือก -->
            <div v-if="showPoll && activePoll" class="poll-panel w-100 mt-4" data-testid="host-poll-panel">
              <div class="poll-panel__header mb-5">
                <div class="poll-panel__header-main">
                  <div class="poll-panel__title-row">
                    <span class="poll-panel__live-badge">{{ activePoll.statusLabel }}</span>
                    <span class="poll-panel__title">{{ activePoll.title }}</span>
                  </div>

                  <div class="poll-panel__question mt-4" data-testid="host-poll-panel-question">
                    {{ activePoll.question }}
                  </div>
                </div>
              </div>

              <div
                v-for="option in activePoll.options"
                :key="option.id || option.optionText"
                class="mb-4"
                data-testid="host-poll-panel-option"
              >
                <div class="poll-panel__item-head mb-2">
                  <span class="poll-panel__option-label d-flex align-center" data-testid="host-poll-panel-label">
                    <span v-if="activePollHasImages" class="poll-panel__thumb-slot mr-3">
                      <img
                        v-if="option.optionImage && !brokenPollImages[option.id]"
                        :src="option.optionImage"
                        alt=""
                        class="poll-panel__option-thumb"
                        data-testid="host-poll-panel-thumb"
                        @error="markPollImageBroken(option.id)"
                      />
                    </span>
                    <span class="poll-panel__option-text">{{ option.optionText }}</span>
                  </span>
                  <span class="poll-panel__option-metrics" data-testid="host-poll-panel-metrics">{{ calculatePercentage(option.voteCount) }}% ({{ option.voteCount || 0 }})</span>
                </div>

                <div class="poll-panel__track">
                  <div
                    class="poll-panel__fill"
                    :class="{ 'poll-panel__fill--primary': option.primary }"
                    data-testid="host-poll-panel-fill"
                    :style="{ width: calculatePercentage(option.voteCount) + '%' }"
                  ></div>
                </div>
              </div>

              <div class="poll-panel__footer">
                <span class="poll-panel__total-votes" data-testid="host-poll-panel-total">{{ $t("livefeature_total_votes") }}: {{ totalVotes }}</span>
                <button
                  type="button"
                  class="poll-panel__close-primary"
                  data-testid="host-poll-panel-close"
                  @click="handleClosePoll"
                >
                  {{ $t("livefeature_close_poll") }}
                </button>
              </div>
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="3" class="pa-2 d-flex host-workspace__sidebar">
          <div class="sidebar-shell">
            <v-tabs
              v-model="activeTab"
              background-color="#ffffff"
              color="#16a34a"
              slider-color="#16a34a"
              class="sidebar-tabs"
              show-arrows
            >
              <v-tab
                v-for="tab in sidebarTabs"
                :key="tab.key"
                class="sidebar-tabs__tab"
                :data-testid="'host-tab-' + tab.key"
              >
                {{ tab.label }}
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab" class="sidebar-tabs-items">
              <v-tab-item>
                <div class="sidebar-panel sidebar-panel--chat pa-4">
                  <div ref="chatPanel" class="chat-messages">
                    <div
                      v-for="(msg, i) in chatMessages"
                      :key="msg.id || i"
                      class="chat-row mb-3 chat-bubble-wrap"
                      :class="msg.mine ? 'chat-row--mine' : 'chat-row--theirs'"
                    >
                      <!-- Body -->
                      <div class="chat-row__body">
                        <!-- Sender name — others only -->
                        <div v-if="!msg.mine" class="chat-row__sender">{{ msg.sender }}</div>

                        <!-- Bubble -->
                        <div class="chat-row__group">
                          <div
                            class="chat-row__bubble"
                            :class="msg.mine ? 'chat-row__bubble--mine' : 'chat-row__bubble--theirs'"
                          >
                            <!-- Reply quote block -->
                            <div
                              v-if="msg.replyTo"
                              class="chat-quote"
                              :class="msg.mine ? 'chat-quote--mine' : ''"
                            >
                              <span class="chat-quote-name">{{ msg.replyTo.name }}</span>
                              <span class="chat-quote-text">{{ msg.replyTo.text }}</span>
                            </div>
                            {{ msg.text }}
                          </div>
                        </div>

                        <!-- Timestamp + reply button -->
                        <div class="chat-row__foot">
                          <span class="chat-row__ts">{{ msg.time }}</span>
                          <v-btn
                            text
                            x-small
                            class="chat-reply-btn"
                            color="#6CC8A0"
                            @click="startReply(msg)"
                          >Reply</v-btn>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="chat-compose">
                    <div v-if="replyingToChatID" class="chat-reply-banner">
                      <div class="chat-reply-banner__content">
                        <v-icon size="14" color="#6CC8A0" class="mr-1">mdi-reply</v-icon>
                        <span class="chat-reply-banner__name">Replying to {{ replyingToName }}</span>
                      </div>
                      <v-btn icon x-small @click="replyingToChatID = null; replyingToName = null; replyingToText = null">
                        <v-icon size="16">mdi-close</v-icon>
                      </v-btn>
                    </div>
                    <v-text-field
                      ref="chatInput"
                      v-model="chatMessage"
                      append-icon="mdi-send"
                      dense
                      outlined
                      rounded
                      hide-details
                      :disabled="sendingChat"
                      :placeholder="$t('livefeature_type_message')"
                      class="chat-input-pill"
                      @click:append="sendChat"
                      @keyup.enter="sendChat"
                    />
                  </div>
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="sidebar-panel pa-4">
                  <div v-if="participantLoading" class="sidebar-empty">
                    <div class="sidebar-empty__title">{{ $t("livefeature_loading_participants") }}</div>
                  </div>

                  <div v-else-if="!participants.length" class="sidebar-empty">
                    <div class="sidebar-empty__title">No participants loaded</div>
                    <div class="sidebar-empty__copy">
                      Participants will appear here after they join the live session.
                    </div>
                  </div>

                  <div
                    v-for="participant in participants"
                    :key="participant.id"
                    class="participant-row mb-3"
                  >
                    <div class="participant-row__left">
                      <v-avatar :color="participant.color" size="34" class="mr-3">
                        <span class="chat-row__initials">{{ participant.initials }}</span>
                      </v-avatar>
                      <div>
                        <div class="participant-row__name">{{ participant.name }}</div>
                        <div class="participant-row__role">{{ participant.role }}</div>
                      </div>
                    </div>

                    <v-chip small label :color="participant.badgeColor" :text-color="participant.badgeText">
                      {{ participant.badge }}
                    </v-chip>
                  </div>
                </div>
              </v-tab-item>

              <v-tab-item>
                <!-- B02: Q&A จริงจาก API (D26/D27/D28) — loading / load error / empty + การ์ดคำถาม -->
                <div class="sidebar-panel pa-4" data-testid="host-qa-panel">
                  <div v-if="!qaLoadedOnce && !qaLoadError" class="sidebar-empty" data-testid="host-qa-loading">
                    <div class="sidebar-empty__title">{{ $t("livefeature_loading_wait") }}</div>
                  </div>
                  <v-alert v-else-if="!qaLoadedOnce && qaLoadError" type="error" dense outlined class="mb-0" data-testid="host-qa-load-error">
                    {{ $t("livefeature_qa_load_failed") }}
                  </v-alert>
                  <div v-else-if="!questions.length" class="sidebar-empty" data-testid="host-qa-empty">
                    <div class="sidebar-empty__title">{{ $t("livefeature_no_qa_items") }}</div>
                    <div class="sidebar-empty__copy">{{ $t("livefeature_qa_empty_copy") }}</div>
                  </div>
                  <div v-for="question in questions" :key="question.id" class="qa-panel pa-4 mb-4" data-testid="host-qa-item">
                    <div class="qa-panel__eyebrow mb-2" :class="{ 'qa-panel__eyebrow--pending': question.status !== 1 }" data-testid="host-qa-badge">{{ question.badge }}</div>
                    <div class="qa-panel__question mb-3" data-testid="host-qa-question">{{ question.question }}</div>
                    <div class="qa-panel__meta mb-4">{{ $t("livefeature_asked_by") }} {{ question.askedBy }} • {{ question.time }}</div>
                    <div class="d-flex flex-wrap">
                      <v-btn v-if="question.status !== 1" color="#16a34a" dark class="qa-panel__action mr-2 mb-2"
                        :loading="qaActionId === question.id && qaActionStatus === 1" :disabled="!!qaActionId"
                        data-testid="host-qa-allow" @click="handleQaApproveSpeaker(question)">{{ $t("livefeature_allow_to_speak") }}</v-btn>
                      <v-btn text color="#475569" class="qa-panel__action mb-2"
                        :loading="qaActionId === question.id && qaActionStatus === 2" :disabled="!!qaActionId"
                        data-testid="host-qa-dismiss" @click="handleQaDismiss(question)">{{ $t("livefeature_dismiss") }}</v-btn>
                    </div>
                  </div>
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="sidebar-panel pa-4">
                  <div class="qa-panel pa-4">
                    <!-- B01: i18n + data-testid -->
                    <div class="qa-panel__eyebrow mb-2">{{ $t("livefeature_poll") }}</div>
                    <div class="qa-panel__question mb-3" data-testid="host-poll-tab-summary">{{ pollPanelSummary }}</div>
                    <div class="qa-panel__meta mb-4" data-testid="host-poll-tab-meta">{{ pollPanelMeta }}</div>
                    <div class="d-flex flex-wrap">
                      <v-btn color="#16a34a" dark class="mr-2 mb-2" :disabled="!activePoll" data-testid="host-poll-show-overlay" @click="showPoll = true">
                        {{ $t("livefeature_show_poll_overlay") }}
                      </v-btn>
                      <v-btn outlined color="#16a34a" class="mb-2" data-testid="host-poll-tab-create" @click="openPollDialog">
                        <v-icon left small>mdi-poll</v-icon>
                        {{ $t("livefeature_create_poll") }}
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-tab-item>

              <v-tab-item>
                <!-- B03 (F4) — รายการบันทึก + สถานะไลบรารี่ (GetRecordingLibraryStatus) -->
                <div class="sidebar-panel pa-4" data-testid="host-rec-panel">
                  <div v-if="recordingsLoading" class="sidebar-empty" data-testid="host-rec-loading">
                    <div class="sidebar-empty__title">{{ $t('livefeature_recordings_loading') }}</div>
                  </div>
                  <v-alert
                    v-else-if="recordingsLoadError && !recordingsLoadedOnce"
                    type="error"
                    dense
                    outlined
                    class="mb-0"
                    data-testid="host-rec-load-error"
                  >
                    {{ $t('livefeature_recordings_load_failed') }}
                    <v-btn
                      text
                      x-small
                      color="#b91c1c"
                      class="host-rec-reload"
                      data-testid="host-rec-reload"
                      @click="fetchRecordingLibraryStatus()"
                    >{{ $t('livefeature_recordings_reload') }}</v-btn>
                  </v-alert>
                  <div v-else-if="!recordings.length" class="sidebar-empty" data-testid="host-rec-empty">
                    <div class="sidebar-empty__title">{{ $t('livefeature_recordings_empty') }}</div>
                    <div class="sidebar-empty__copy">{{ $t('livefeature_recordings_empty_hint') }}</div>
                  </div>
                  <template v-else>
                    <div
                      v-for="(rec, index) in recordings"
                      :key="rec.liveSessionRecordID"
                      class="participant-row host-rec-row mb-3"
                      data-testid="host-rec-row"
                      :data-record-id="rec.liveSessionRecordID"
                    >
                      <div class="participant-row__name">{{ $t('livefeature_recording_item', { n: index + 1 }) }}</div>
                      <div class="participant-row__role host-rec-row__meta" data-testid="host-rec-meta">
                        {{ formatRecordingDate(rec.createdDate) }} · {{ rec.duration || '—' }} · {{ rec.fileSizeMB ? rec.fileSizeMB + ' MB' : '—' }}
                      </div>
                      <LiveRecordingLibraryBadge
                        :item="rec"
                        :can-retry="check_Permissions('live')"
                        :retrying="retryingRecordId === rec.liveSessionRecordID"
                        :disabled="isRetryingImport"
                        @retry="retryRecordingImport"
                      />
                    </div>
                  </template>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="showEndLiveDialog" max-width="420">
      <v-card class="rounded-xl">
        <v-card-title class="text-h6">{{ $t("livefeature_end_live_question") }}</v-card-title>
        <!-- B03 (F3/D27) — แจ้งว่าวิดีโอจะเข้าไลบรารี่เมื่อมีการบันทึก -->
        <v-card-text v-if="hadRecording" class="pb-0 host-end-dialog__note" data-testid="host-end-dialog-recording-note">
          <v-icon small color="#16a34a" class="mr-1">mdi-video-outline</v-icon>{{ $t('livefeature_recording_library_pending') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="#64748b" @click="showEndLiveDialog = false">{{ $t("livefeature_cancel") }}</v-btn>
          <v-btn color="#dc2626" dark data-testid="host-end-confirm" @click="handleEndLive">End</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- B01: scrollable ให้ header / alert / ปุ่มล่างไม่ถูก scroll หาย -->
    <v-dialog v-model="showPollDialog" max-width="760" persistent scrollable>
      <v-card class="poll-dialog rounded-2xl" data-testid="host-poll-dialog">
        <v-card-title class="poll-dialog__header">
          <div class="poll-dialog__header-left">
            <v-icon color="#16a34a" class="mr-2">mdi-poll</v-icon>
            <div class="poll-dialog__title">{{ $t("livefeature_create_live_poll") || 'Create a Live Poll' }}</div>
          </div>
          <v-btn icon color="#64748b" @click="resetPollCreator()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="poll-dialog__body">
          <!-- B01: input ไฟล์รูปตัวเลือก (ซ่อน) — ไม่ใช้ image/* (D7) -->
          <input
            ref="pollOptionImageInput"
            type="file"
            accept=".jpg,.jpeg,.png,.gif,.bmp,image/jpeg,image/png,image/gif,image/bmp"
            class="d-none"
            data-testid="host-poll-image-input"
            @change="handlePollOptionImageSelected"
          >
          <div class="field-block mb-5">
            <label class="poll-dialog__label" for="poll-question">
              {{ $t("livefeature_poll_question") || 'Question' }}
              <span class="poll-dialog__required">*</span>
            </label>
            <v-text-field
              id="poll-question"
              v-model="draftPollQuestion"
              outlined
              dense
              hide-details="auto"
              class="poll-dialog__input"
              data-testid="host-poll-question-input"
              :placeholder="$t('livefeature_poll_question_placeholder')"
            />
          </div>

          <div class="field-block mb-5">
            <label class="poll-dialog__label">{{ $t("livefeature_answer_type") || 'Answer type' }}</label>
            <div class="poll-dialog__answer-type mt-2">
              <button
                type="button"
                class="poll-dialog__type-chip"
                :class="{ 'poll-dialog__type-chip--active': draftPollType === 1 }"
                data-testid="host-poll-type-single"
                @click="draftPollType = 1"
              >
                <v-icon left small :color="draftPollType === 1 ? '#fff' : '#333'">mdi-radiobox-marked</v-icon>
                {{ $t("livefeature_single_choice") }}
              </button>
              <button
                type="button"
                class="poll-dialog__type-chip"
                :class="{ 'poll-dialog__type-chip--active': draftPollType === 2 }"
                data-testid="host-poll-type-multiple"
                @click="draftPollType = 2"
              >
                <v-icon left small :color="draftPollType === 2 ? '#fff' : '#333'">mdi-checkbox-marked-outline</v-icon>
                {{ $t("livefeature_multiple_choice") }}
              </button>
            </div>
          </div>

          <div class="poll-dialog__options-head mb-3">
            <div class="poll-dialog__label mb-0">
              {{ $t("livefeature_answer_options") || 'Options' }}
              <div class="poll-dialog__options-hint-inline mt-1">{{ $t('livefeature_option_image_hint') }}</div>
            </div>
          </div>

          <div
            v-for="(option, index) in draftPollOptions"
            :key="option.id"
            class="poll-dialog__option-row mb-3"
            data-testid="host-poll-option-row"
          >
            <span class="poll-dialog__option-num">{{ index + 1 }}</span>
            <v-text-field
              v-model="option.label"
              outlined
              dense
              hide-details="auto"
              class="poll-dialog__option-input"
              data-testid="host-poll-option-input"
              :placeholder="`${$t('livefeature_option')} ${index + 1}`"
            />
            <!-- B01: ช่องรูปตัวเลือก (ไม่มีรูป / กำลังอัป / มีรูป) -->
            <div class="poll-dialog__option-image" data-testid="host-poll-option-image-cell">
              <v-progress-circular
                v-if="option.isUploading"
                indeterminate
                size="20"
                width="2"
                color="#16a34a"
                data-testid="host-poll-option-uploading"
              />
              <template v-else-if="option.imagePreview">
                <img
                  class="poll-dialog__option-thumb"
                  data-testid="host-poll-option-thumb"
                  :src="option.imagePreview"
                  alt=""
                >
                <v-btn
                  icon
                  x-small
                  data-testid="host-poll-option-remove-image"
                  :title="$t('livefeature_remove_image')"
                  :aria-label="$t('livefeature_remove_image')"
                  :disabled="isPollSubmitting"
                  @click="removePollOptionImage(option.id)"
                >
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </template>
              <v-btn
                v-else
                icon
                small
                color="#94a3b8"
                data-testid="host-poll-option-attach"
                :title="$t('livefeature_attach_image')"
                :aria-label="$t('livefeature_attach_image')"
                :disabled="isPollImageUploading || isPollSubmitting"
                @click="openPollOptionImagePicker(option.id)"
              >
                <v-icon small>mdi-image-plus</v-icon>
              </v-btn>
            </div>
            <v-btn
              icon
              small
              color="#94a3b8"
              data-testid="host-poll-option-remove"
              :disabled="draftPollOptions.length <= 2"
              @click="removePollOption(option.id)"
            >
              <v-icon small>mdi-close-circle-outline</v-icon>
            </v-btn>
          </div>

          <button
            type="button"
            class="poll-dialog__add-option"
            data-testid="host-poll-add-option"
            :disabled="!canAddPollOption"
            @click="addPollOption"
          >
            + {{ $t("livefeature_add_option") || 'Add option' }}
          </button>
          <div v-if="!canAddPollOption" class="poll-dialog__options-limit" data-testid="host-poll-options-limit">
            {{ $t('livefeature_max_poll_options', [10]) }}
          </div>

          <div class="poll-dialog__settings-section mt-6">
            <div class="poll-dialog__settings-header mb-4">
              <v-icon small color="#16a34a" class="mr-2">mdi-cog</v-icon>
              <span class="poll-dialog__settings-title">{{ $t("livefeature_poll_settings") || 'Poll settings' }}</span>
            </div>

            <div class="poll-dialog__setting-row">
              <div class="poll-dialog__setting-info">
                <h4 class="poll-dialog__setting-name">{{ $t("livefeature_answer_timer") || 'Answer timer' }}</h4>
                <p class="poll-dialog__setting-desc">
                  {{ $t("livefeature_answer_timer_help") || 'Count down so answers must be submitted within the set time' }}
                </p>
              </div>
              <div class="poll-dialog__setting-control">
                <input
                  v-model.number="answerTimerSeconds"
                  type="number"
                  min="1"
                  step="1"
                  class="poll-dialog__timer-input"
                >
                <span class="poll-dialog__timer-unit">{{ $t("livefeature_seconds") || 'seconds' }}</span>
              </div>
            </div>

            <div class="poll-dialog__setting-row">
              <div class="poll-dialog__setting-info">
                <h4 class="poll-dialog__setting-name">{{ $t("livefeature_anonymous_responses") || 'Anonymous responses' }}</h4>
                <p class="poll-dialog__setting-desc">
                  {{ $t("livefeature_anonymous_help") || 'Keep only the totals, without linking who chose what' }}
                </p>
              </div>
              <v-switch
                v-model="isAnonymous"
                color="#4fd1c5"
                hide-details
                class="poll-settings__switch mt-0 flex-shrink-0 pt-0"
              />
            </div>

            <div class="poll-dialog__setting-row poll-dialog__setting-row--last">
              <div class="poll-dialog__setting-info">
                <h4 class="poll-dialog__setting-name">{{ $t("livefeature_show_results_closed") }}</h4>
                <p class="poll-dialog__setting-desc">
                  {{ $t("livefeature_show_results_closed_help") }}
                </p>
              </div>
              <v-switch
                v-model="showResultsAfterClose"
                color="#4fd1c5"
                hide-details
                class="poll-settings__switch mt-0 flex-shrink-0 pt-0"
              />
            </div>
          </div>
        </v-card-text>

        <!-- B01: error ของ dialog อยู่นอกส่วนที่ scroll (D14) -->
        <div v-if="pollDialogError" class="poll-dialog__error">
          <v-alert type="error" dense outlined class="mb-0" data-testid="host-poll-dialog-error">{{ pollDialogError }}</v-alert>
        </div>

        <v-card-actions class="poll-dialog__actions">
          <v-btn text color="#64748b" data-testid="host-poll-cancel" @click="resetPollCreator()">{{ $t("livefeature_cancel") || 'Cancel' }}</v-btn>
          <v-spacer />
          <v-btn
            color="#1e293b"
            dark
            :loading="isPollSubmitting"
            :disabled="isPollSubmitting || isPollImageUploading"
            data-testid="host-poll-start"
            @click="handleStartPoll"
          >
            <v-icon left small>mdi-play</v-icon>
            {{ $t("livefeature_start_poll") || 'Start Poll (open for voting immediately)' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== Breakout Rooms Dialog ===== -->
    <v-dialog v-model="showBreakoutDialog" max-width="640" persistent>
      <v-card class="breakout-dialog rounded-xl">
        <v-card-title class="breakout-dialog__header">
          <v-icon color="#16a34a" class="mr-2">mdi-account-multiple-plus</v-icon>
          <span class="breakout-dialog__title">{{ isInBreakoutRoom ? 'Manage Breakout Rooms' : 'Create Breakout Rooms' }}</span>
          <v-spacer />
          <v-btn icon color="#64748b" :disabled="breakoutLoading" @click="showBreakoutDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Create form — shown when no breakout is active -->
          <div v-if="!isInBreakoutRoom">
            <div class="breakout-dialog__field-label mb-1">Number of rooms</div>
            <v-slider
              v-model="breakoutRoomCount"
              :min="2"
              :max="10"
              :step="1"
              thumb-label="always"
              color="#16a34a"
              track-color="#e2e8f0"
              class="mb-4"
            />
            
            <!-- Assignment mode toggle -->
            <div class="d-flex align-center mb-4">
              <v-btn-toggle v-model="showManualAssignment" mandatory dense borderless class="breakout-toggle">
                <v-btn :value="false" small>
                  <v-icon left x-small>mdi-shuffle</v-icon>
                  Auto-assign
                </v-btn>
                <v-btn :value="true" small>
                  <v-icon left x-small>mdi-account-edit</v-icon>
                  Manual assign
                </v-btn>
              </v-btn-toggle>
            </div>

            <!-- Auto-assignment hint -->
            <div v-if="!showManualAssignment" class="breakout-dialog__hint">
              {{ availableParticipants.length }} participants will be split across {{ breakoutRoomCount }} rooms
              (~{{ Math.ceil(availableParticipants.length / breakoutRoomCount) }} per room).
            </div>

            <!-- Manual assignment UI -->
            <div v-else class="manual-assignment">
              <div class="manual-assignment__hint mb-3">
                Assign {{ availableParticipants.length }} participants to rooms manually
              </div>
              
              <!-- Room columns -->
              <div class="manual-assignment__grid">
                <div 
                  v-for="roomIdx in breakoutRoomCount" 
                  :key="roomIdx"
                  class="room-column"
                >
                  <div class="room-column__header">
                    <v-icon small color="#16a34a" class="mr-1">mdi-door</v-icon>
                    <span>Room {{ roomIdx }}</span>
                    <v-chip x-small label color="#dbeafe" text-color="#1d4ed8" class="ml-auto">
                      {{ getRoomParticipantCount(roomIdx - 1) }}
                    </v-chip>
                  </div>
                  <div class="room-column__participants">
                    <div
                      v-for="participant in getParticipantsForRoom(roomIdx - 1)"
                      :key="participant.id"
                      class="participant-chip"
                    >
                      <div class="participant-chip__avatar">{{ participant.initials }}</div>
                      <span class="participant-chip__name">{{ participant.name }}</span>
                      <v-btn icon x-small @click="unassignParticipant(participant.id)">
                        <v-icon x-small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Unassigned participants -->
              <div v-if="unassignedParticipants.length" class="unassigned-pool mt-4">
                <div class="unassigned-pool__header">
                  <v-icon small color="#64748b" class="mr-1">mdi-account-group</v-icon>
                  <span>Unassigned ({{ unassignedParticipants.length }})</span>
                </div>
                <div class="unassigned-pool__participants">
                  <div
                    v-for="participant in unassignedParticipants"
                    :key="participant.id"
                    class="participant-chip participant-chip--unassigned"
                  >
                    <div class="participant-chip__avatar">{{ participant.initials }}</div>
                    <span class="participant-chip__name">{{ participant.name }}</span>
                    <v-menu offset-y>
                      <template #activator="{ on }">
                        <v-btn icon x-small color="#16a34a" v-on="on">
                          <v-icon x-small>mdi-plus</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-list-item
                          v-for="roomIdx in breakoutRoomCount"
                          :key="roomIdx"
                          @click="assignParticipant(participant.id, roomIdx - 1)"
                        >
                          <v-list-item-title>Room {{ roomIdx }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Active breakout rooms list -->
          <div v-else>
            <v-alert type="success" dense text class="mb-4">
              Breakout rooms are active. Participants have been notified.
            </v-alert>
            <div
              v-for="(room, idx) in breakoutRooms"
              :key="room.id || idx"
              class="breakout-room-row mb-3"
            >
              <div class="breakout-room-row__info">
                <v-icon small color="#16a34a" class="mr-2">mdi-door</v-icon>
                <span class="breakout-room-row__name">{{ room.roomName || `Room ${idx + 1}` }}</span>
                <v-chip x-small label color="#dbeafe" text-color="#1d4ed8" class="ml-2">
                  {{ room.participantCount || 0 }} participants
                </v-chip>
              </div>
              <v-btn small outlined color="#16a34a" @click="joinBreakoutRoom(room)">
                <v-icon left x-small>mdi-login</v-icon>
                Monitor
              </v-btn>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text color="#64748b" :disabled="breakoutLoading || breakoutEndLoading" @click="showBreakoutDialog = false">
            Close
          </v-btn>
          <v-btn
            v-if="!isInBreakoutRoom"
            color="#16a34a"
            dark
            :loading="breakoutLoading"
            :disabled="breakoutLoading"
            @click="createBreakoutRooms"
          >
            <v-icon left small>mdi-play</v-icon>
            Start Breakout
          </v-btn>
          <v-btn
            v-if="isInBreakoutRoom"
            color="#dc2626"
            dark
            :loading="breakoutEndLoading"
            :disabled="breakoutEndLoading"
            @click="endBreakoutRooms"
          >
            <v-icon left small>mdi-stop</v-icon>
            End All Rooms
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Breakout "return to main" banner -->
    <v-snackbar
      v-model="isInBreakoutRoom"
      :timeout="-1"
      color="#1e293b"
      bottom
      right
    >
      <v-icon small color="#a3e635" class="mr-2">mdi-door-open</v-icon>
      Monitoring a breakout room
      <template #action>
        <v-btn small text color="#a3e635" :loading="breakoutLoading" @click="returnToMainRoom">
          Return to Main
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { Room, Track } from "livekit-client";
import LiveRecordingLibraryBadge from "@/components/LiveRecordingLibraryBadge.vue"; // B03

const MAX_POLL_OPTIONS = 10; // B01 (Q2/D23) — จำนวนตัวเลือกโพลสูงสุด

export default {
  name: "LiveHost",
  components: { LiveRecordingLibraryBadge }, // B03

  async created() {
    if (!this.check_Permissions("live")) {
      this.$router.replace("/home/dashboard");
      return;
    }
    await this.initializeHostStudio();
  },

  mounted() {
    this.chatEncoder = new TextEncoder();
    this.chatDecoder = new TextDecoder();
    this.chatSeenIds = new Set();
    this.startChatPolling();
    this.startQaPolling(); // B02 (D11) — ตัวเลขแท็บ Q&A อัปเดตแม้ไม่ได้เปิดแท็บ
  },

  beforeDestroy() {
    this.stopChatPolling();
    this.stopPollResultsPolling(); // B01
    this.stopQaPolling(); // B02 — หยุดที่นี่เท่านั้น ไม่ใส่ใน disconnectLiveRoom() (C13)
    this.disconnectLiveRoom();
    this.stopSessionTimer();
    this.stopAutoRecordWatch(); // B03
    this.stopRecordingsRefresh(); // B03
  },

  data() {
    const auth = JSON.parse(localStorage.getItem("vivek_authenticated_user_data") || "null");
    const localUserId = parseInt(localStorage.getItem("UserID"), 10);

    return {
      activeTab: 0,
      showPoll: false,
      showPollDialog: false,
      showEndLiveDialog: false,
      chatMessage: "",
      sendingChat: false,
      chatEncoder: null,
      chatDecoder: null,
      chatSeenIds: null,
      chatPollInterval: null,
      replyingToChatID: null,
      replyingToName: null,
      replyingToText: null,
      isLoading: false,
      isEndingLive: false,
      isPollSubmitting: false,
      participantLoading: false,
      chatLoading: false,
      pollLoading: true, // B01 — เริ่มเป็น true เพื่อแสดง "กำลังโหลด" ในแท็บ Poll จนกว่า fetchSessionPolls จะจบ
      muteAllLoading: false,
      isRecordingLoading: false,
      isConnectingRoom: false,
      roomToken: "",
      liveKitUrl: "",
      roomId: "",
      liveKitRoom: null,
      remoteVideoTracks: [],
      errorMessage: "",
      infoMessage: "",
      currentAdminMemberId: auth?.memberId || "",
      currentAdminUserId: Number.isNaN(localUserId) ? 0 : localUserId,
      currentAdminUserName: auth?.fullName || "",
      draftPollQuestion: "",
      draftPollType: 1,
      answerTimerSeconds: 30,
      isAnonymous: false,
      showResultsAfterClose: false,
      nextPollOptionId: 3,
      draftPollOptions: [
        { id: 1, label: "", isCorrect: false, imageUrl: "", imagePreview: "", isUploading: false },
        { id: 2, label: "", isCorrect: false, imageUrl: "", imagePreview: "", isUploading: false },
      ],
      // B01 — แนบรูปตัวเลือก / error ใน dialog / refresh ผลโหวต
      pollImageTargetId: null,
      pollDraftVersion: 0,
      pollDialogError: "",
      pollResultsInterval: null,
      pollResultsInFlight: false,
      brokenPollImages: {},
      sessionMeta: {
        title: this.$t("livefeature_host_studio"),
        subtitle: this.$t("livefeature_live_host_studio"),
        statusLabel: this.$t("livefeature_preparing"),
        status: "pending",
        autoRecord: false,
        startTime: "",
        endTime: "",
        shareTitle: this.$t("livefeature_screen_sharing_ready"),
        shareSubtitle: this.$t("livefeature_share_prompt"),
        hostName: auth?.fullName || this.$t("livefeature_host_label"),
      },
      hostStats: {
        watchingCount: 0,
      },
      isRecording: false,
      hasStoppedRecording: false,
      isRecordingCooldown: false,
      recordingWaitSeconds: 0,
      // B03 — เฝ้าการเริ่ม auto-record (F2) + สถานะไลบรารี่ของการบันทึก (F4) + ปุ่มลองใหม่
      autoRecordWatchState: "idle", // idle | starting | not_started
      autoRecordWatchTimer: null,
      autoRecordWatchCount: 0,
      autoRecordPollBusy: false,
      recordingsLoadedOnce: false,
      recordingsLoadError: false,
      recordingsRefreshTimer: null,
      recordingsRefreshCount: 0,
      isRetryingImport: false,
      retryingRecordId: "",
      timerIntervalId: null,
      sessionElapsedSeconds: 0,
      controls: [
        { icon: "mdi-microphone", label: "Mic", action: "mic" },
        { icon: "mdi-video", label: "Camera", action: "camera" },
        { icon: "mdi-monitor-share", label: "Share", action: "share" },
        { icon: "mdi-draw", label: "Draw", action: "draw" },
        { icon: "mdi-poll", label: "Create Poll", action: "create-poll" },
        { icon: "mdi-account-group", label: "People", action: "people" },
        { icon: "mdi-account-multiple-plus", label: "Breakout", action: "breakout-rooms" },
        { icon: "mdi-volume-mute", label: "Mute All", action: "mute-all" },
        { icon: "mdi-cog", label: "Settings", action: "settings" },
      ],
      embedUrl: "",
      embedInputDraft: "",
      embedActive: false,
      isDrawing: false,
      lastX: 0,
      lastY: 0,
      controlState: {
        mic: true,
        camera: true,
        share: false,
        draw: false,
        people: false,
        "mute-all": false,
        settings: false,
      },
      hostUsers: [],
      chatMessages: [],
      participants: [],
      questions: [],
      // B02 — Q&A polling + moderation (D16, D27, D28)
      qaPollInterval: null,
      qaFetching: false,
      qaLoadedOnce: false,
      qaLoadError: false,
      qaActionId: null,
      qaActionStatus: null, // 1 = Allow, 2 = Dismiss (ปุ่มไหนหมุน)
      qaSeq: 0,
      activePoll: null,
      recordings: [],
      recordingsLoading: false,
      // Breakout rooms state
      showBreakoutDialog: false,
      breakoutRoomCount: 2,
      breakoutRooms: [],
      isInBreakoutRoom: false,
      currentBreakoutRoomId: null,
      mainRoomToken: "",
      mainLiveKitUrl: "",
      breakoutLoading: false,
      breakoutEndLoading: false,
      // Manual room assignments: { participantId: roomIndex }
      manualRoomAssignments: {},
      showManualAssignment: false,
      // Reactive list of currently-connected learners (kept in sync via LiveKit events)
      connectedParticipants: [],
    };
  },

  computed: {
    currentSessionId() {
      return this.$route.params.roomId;
    },

    // All camera tiles currently rendered in the conference grid:
    // the host's own tile (always present) plus every remote participant feed.
    activeParticipants() {
      return [{ sid: "self", isSelf: true }, ...this.remoteVideoTracks];
    },

    // Google Meet style dynamic grid sizing. Drives the `--card-min-width`
    // CSS variable consumed by `.conf-grid`'s auto-fit columns so the grid
    // auto-scales to the participant count without ever scrolling.
    gridStyleVars() {
      const count = this.activeParticipants.length;

      let cardMinWidth = "30%";
      let gridTemplateRows = null;

      if (count <= 1) {
        // Single focal display fills the stage.
        cardMinWidth = "90%";
      } else if (count === 2) {
        // Two tiles share the width and stretch to the full height.
        cardMinWidth = "45%";
        gridTemplateRows = "1fr";
      } else if (count <= 4) {
        // Clean 2x2 grid.
        cardMinWidth = "45%";
      } else if (count <= 9) {
        // Tight 3x3 grid.
        cardMinWidth = "30%";
      } else {
        // 4x4 (or denser) layout for large rooms.
        cardMinWidth = "22%";
      }

      const style = { "--card-min-width": cardMinWidth };
      if (gridTemplateRows) {
        style["grid-template-rows"] = gridTemplateRows;
      }
      return style;
    },

    liveTimerStorageKey() {
      return this.currentSessionId ? `live-session-start-${this.currentSessionId}` : "";
    },

    startLiveEndpoint() {
      return this.currentSessionId
        ? `${this.web_url}LiveSession/StartLive/${this.currentSessionId}`
        : "";
    },

    getRoomTokenEndpoint() {
      return this.currentSessionId
        ? `${this.web_url}LiveSession/GetRoomToken`
        : "";
    },

    startRecordingEndpoint() {
      return this.currentSessionId
        ? `${this.web_url}LiveSession/StartRecording`
        : "";
    },

    stopRecordingEndpoint() {
      // B03 (F1/E17) — route ต้องมี sessionId (เดิมไม่มี → 404)
      return this.currentSessionId
        ? `${this.web_url}LiveSession/StopRecording/${this.currentSessionId}`
        : "";
    },

    addLivePollEndpoint() {
      return this.currentSessionId ? `${this.web_url}LiveSession/AddLivePoll` : "";
    },

    endLiveEndpoint() {
      return this.currentSessionId
        ? `${this.web_url}LiveSession/EndLive/${this.currentSessionId}`
        : "";
    },

    sessionPollsEndpoint() {
      return this.currentSessionId
        ? `${this.web_url}LiveSession/GetSessionPolls/${this.currentSessionId}`
        : "";
    },

    sessionRecordingsEndpoint() {
      return this.currentSessionId
        ? `${this.web_url}LiveSession/GetSessionRecordings/${this.currentSessionId}`
        : "";
    },

    // B03 (F4) — สถานะไลบรารี่ของการบันทึก / ปุ่มลองใหม่ (v1, ผู้เรียกจาก token — D21)
    recordingLibraryStatusEndpoint() {
      return this.currentSessionId
        ? `${this.web_url}LiveSession/GetRecordingLibraryStatus/${this.currentSessionId}`
        : "";
    },

    recordingImportEndpoint() {
      return this.currentSessionId
        ? `${this.web_url}LiveSession/ImportRecordingsToLibrary/${this.currentSessionId}`
        : "";
    },

    muteAllEndpoint() {
      return `${this.web_url}LiveSession/MuteAllLearners`;
    },

    createBreakoutRoomsEndpoint() {
      return `${this.web_url}LiveSession/CreateBreakoutRooms`;
    },

    getBreakoutRoomTokenEndpoint() {
      return `${this.web_url}LiveSession/GetBreakoutRoomToken`;
    },

    endBreakoutRoomsEndpoint() {
      return `${this.web_url}LiveSession/EndBreakoutRooms`;
    },

    getBreakoutRoomsEndpoint() {
      return this.currentSessionId
        ? `${this.web_url}LiveSession/GetBreakoutRooms/${this.currentSessionId}`
        : "";
    },

    sessionChatEndpoint() {
      return this.currentSessionId
        ? `${this.web_url}LiveSession/GetSessionChat/${this.currentSessionId}`
        : "";
    },

    addSessionChatEndpoint() {
      return `${this.web_url}LiveSession/AddSessionChat`;
    },

    addSessionChatReplyEndpoint() {
      return `${this.web_url}LiveSession/AddSessionChatReply`;
    },

    // B02 — Q&A ของไลฟ์ (ผู้ดูแลมาจาก token — ไม่ส่ง hostUserID: D9)
    sessionQAEndpoint() {
      return this.currentSessionId
        ? `${this.web_url}LiveSession/GetSessionQA/${this.currentSessionId}`
        : "";
    },

    updateSessionQAStatusEndpoint() {
      return `${this.web_url}LiveSession/UpdateSessionQAStatus`;
    },

    sessionDetailEndpoint() {
      return this.currentSessionId
        ? `${this.web_url}LiveSession/GetSessionById/${this.currentSessionId}`
        : "";
    },

    headerTitle() {
      return this.sessionMeta.title || `Live Session #${this.currentSessionId}`;
    },

    headerSubtitle() {
      return this.sessionMeta.subtitle || this.$t("livefeature_live_host_studio");
    },

    statusBadgeLabel() {
      return `${this.sessionMeta.statusLabel} • ${this.sessionTimerLabel}`;
    },

    statusBadgeColor() {
      return this.sessionMeta.status === "ended" ? "#64748b" : "#dc2626";
    },

    watchingLabel() {
      return `${this.hostStats.watchingCount || 0} ${this.$t("livefeature_watching")}`;
    },

    stageTitle() {
      return this.sessionMeta.shareTitle || this.$t("livefeature_screen_sharing_ready");
    },

    stageSubtitle() {
      return this.sessionMeta.shareSubtitle || this.$t("livefeature_share_prompt");
    },

    stageIcon() {
      return this.controlState.share ? "mdi-monitor-share" : "mdi-monitor-screenshot";
    },

    currentAdminInitials() {
      return this.buildInitials(this.currentAdminUserName || this.sessionMeta.hostName || this.$t("livefeature_host_label"));
    },

    currentAdminPreviewLabel() {
      return `${this.currentAdminUserName || this.sessionMeta.hostName || this.$t("livefeature_host_label")} (${this.$t("you")})`;
    },

    recordingButtonLabel() {
      if (this.isRecordingCooldown) {
        return this.recordingWaitSeconds > 0
          ? `Waiting... (${this.recordingWaitSeconds}s)`
          : "Stopping...";
      }
      if (this.isRecording) return "Stop Recording";
      return "Start Recording";
    },

    // B03 (D24/E58) — ชื่อปุ่มตรงกับที่แสดงจริง
    autoRecordNotStartedText() {
      return this.$t("livefeature_auto_record_not_started", { button: this.recordingButtonLabel });
    },

    // B03 (D27) — ไม่ดูค่าเปิด auto-record ของ session (ไลฟ์ที่ auto-record ไม่เริ่มต้องไม่ได้ข้อความหลอก)
    hadRecording() {
      return this.isRecording || this.hasStoppedRecording || this.recordings.some((r) => r.recordStatus !== 2);
    },

    // B03 (F4, D31) — แถว eligible === false ไม่ทำให้ refresh
    hasProcessingRecording() {
      return this.recordings.some((r) => r.eligible !== false &&
        (r.recordStatus === 0 || (r.recordStatus === 1 && [null, undefined, 0, 1].includes(r.libraryStatus))));
    },

    // B03 (F4) — ใช้ key ของแท็บ ไม่ใช้ index
    recordingsTabActive() {
      return this.sidebarTabs[this.activeTab]?.key === "recordings";
    },

    breadcrumbs() {
      return [
        {
          sidebar_tilte: "home",
        },
        {
          text: "live",
          href: "/home/live",
        },
        {
          text: this.$t("livefeature_host_studio"),
        },
      ];
    },

    sidebarTabs() {
      return [
        { key: "chat", label: "Chat" },
        { key: "participants", label: `${this.$t("livefeature_participants_label")} (${this.participants.length})` },
        { key: "qa", label: `Q&A (${this.questions.length})` },
        { key: "poll", label: this.$t("livefeature_poll") }, // B01
        { key: "recordings", label: `Recordings (${this.recordings.length})` },
      ];
    },

    totalVotes() {
      return (this.activePoll?.options || []).reduce(
        (total, option) => total + Number(option.voteCount || 0),
        0
      );
    },

    sessionTimerLabel() {
      const totalSeconds = Math.max(0, Number(this.sessionElapsedSeconds || 0));
      const minutes = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, "0");
      const seconds = (totalSeconds % 60).toString().padStart(2, "0");

      return `${minutes}:${seconds}`;
    },

    pollPanelSummary() {
      // B01 — i18n + สถานะกำลังโหลด (S1)
      if (!this.activePoll) {
        return this.pollLoading
          ? this.$t("livefeature_loading_wait")
          : this.$t("livefeature_no_active_poll");
      }

      return `${this.activePoll.question} (${this.$t("livefeature_poll_option_count", [this.activePoll.options.length])})`;
    },

    pollPanelMeta() {
      // B01 — i18n + สถานะกำลังโหลด (S1)
      if (!this.activePoll) {
        return this.pollLoading ? "" : this.$t("livefeature_create_poll_now");
      }

      return this.$t("livefeature_review_votes_realtime", [this.totalVotes]);
    },

    // B01 — มีตัวเลือกที่กำลังอัปโหลดรูปอยู่ (อัปได้ทีละรูป — D16)
    isPollImageUploading() {
      return this.draftPollOptions.some((option) => option.isUploading);
    },

    // B01 — จำกัดตัวเลือกโพลไม่เกิน MAX_POLL_OPTIONS (D23)
    canAddPollOption() {
      return this.draftPollOptions.length < MAX_POLL_OPTIONS;
    },

    // B01 — มีรูปอย่างน้อย 1 ตัวเลือก → ทุกแถวในแผงผลมีช่องรูป (จัดแนว)
    activePollHasImages() {
      return (this.activePoll?.options || []).some((option) => !!option.optionImage);
    },

    // Available participants for breakout assignment
    availableParticipants() {
      // Prefer the reactive list of live-connected learners (updated by LiveKit events)
      if (this.connectedParticipants.length) {
        return this.connectedParticipants;
      }
      // Fall back to this.participants (enrolled list) if no one is connected yet
      return this.participants.map((p) => ({
        id: p.id,
        userID: p.userID,
        name: p.name,
        initials: p.initials,
      }));
    },

    // Unassigned participants
    unassignedParticipants() {
      return this.availableParticipants.filter(
        (p) => this.manualRoomAssignments[p.id] === undefined
      );
    },
  },

  watch: {
    // B03 (F2) — กด Start เองสำเร็จ / poll เห็นว่ากำลังอัด → ป้าย auto-record หาย
    isRecording(value) {
      if (value) this.stopAutoRecordWatch();
    },
    // B03 (F4) — ใช้ key ของแท็บ ไม่ใช้ index
    activeTab(val) {
      if (this.sidebarTabs[val]?.key === "recordings") {
        this.fetchRecordingLibraryStatus({ silent: this.recordingsLoadedOnce });
        this.startRecordingsRefresh();
      } else {
        this.stopRecordingsRefresh();
      }
    },
  },

  methods: {
    async initializeHostStudio() {
      await this.handleAdminEnterHostRoom();
      await this.fetchHostSessionContext();

      if (this.roomToken && this.liveKitUrl) {
        await this.connectToLiveRoom();
      }

      await Promise.all([
        this.fetchHostParticipants(),
        this.fetchSessionPolls(),
        this.fetchSessionRecordings(),
      ]);

      this.startPollResultsPolling(); // B01 — refresh ผลโหวตทุก 3 วิ

      await this.fetchRecordingLibraryStatus(); // B03 (F4) — ต้องรู้ก่อนว่ามีแถว recordStatus 1/2 หรือยัง (UX-Q2)
      this.watchAutoRecordStart(); // B03 (F2)

      // Restore active breakout state after a page reload so the dialog
      // shows "Manage Breakout Rooms" instead of "Create Breakout Rooms".
      await this.restoreBreakoutRooms();
    },

    /**
     * Unified room-connection routine used for both the initial main-session
     * join and every subsequent breakout-room hop.
     *
     * Design principles:
     *  1. Explicit teardown FIRST — the old room is fully disconnected and all
     *     reactive state is cleared before the new Room() is created.
     *  2. All LiveKit event handlers close over the local `room` variable, NOT
     *     `this.liveKitRoom`. This matters because LiveKit fires events during
     *     the `await room.connect()` call, at which point `this.liveKitRoom` is
     *     still null. Using the closure keeps handlers valid throughout the
     *     entire connection lifecycle.
     *  3. After `room.connect()` resolves, we walk the existing
     *     `room.remoteParticipants` map and hydrate the tile grid immediately.
     *     This is the critical step that makes learners visible when the host
     *     hops into a breakout room where students are already present.
     */
    async connectRoom(url, token) {
      if (!url || !token) return;

      // ── Step 1: Tear down the previous room completely ──────────────────
      if (this.liveKitRoom) {
        const prev = this.liveKitRoom;
        // Null the reference first so the old room's async "disconnected"
        // event cannot clobber the state we are about to build.
        this.liveKitRoom = null;
        this.connectedParticipants = [];
        this.clearRemoteMedia();
        try { await prev.disconnect(); } catch (_) { /* ignore stale close errors */ }
      }

      this.isConnectingRoom = true;

      try {
        // ── Step 2: Create a fresh Room and bind ALL events BEFORE connecting
        const room = new Room();

        // A new learner joins after the host entered the room
        room.on("participantConnected", (participant) => {
          this.hostStats.watchingCount = Math.max(0, room.remoteParticipants.size);
          this._upsertConnectedParticipant(participant);
          // Attach any tracks already published at connection time
          participant.trackPublications.forEach((pub) => {
            if (pub.isSubscribed && pub.track) this.attachRemoteTrack(pub.track, participant);
          });
        });

        // A learner leaves the room
        room.on("participantDisconnected", (participant) => {
          this.hostStats.watchingCount = Math.max(0, room.remoteParticipants.size);
          this.connectedParticipants = this.connectedParticipants.filter(
            (p) => p.id !== participant.sid
          );
          this.removeRemoteParticipantMedia(participant?.sid);
        });

        // The room session ended (network loss, explicit leave, or server close)
        room.on("disconnected", () => {
          // Ignore stale events from a replaced room instance (e.g. after a
          // breakout hop — the old room disconnects after the new one is set).
          if (this.liveKitRoom !== null && this.liveKitRoom !== room) return;
          this.clearRemoteMedia();
          this.connectedParticipants = [];
          this.liveKitRoom = null;
          this.hostStats.watchingCount = 0;
        });

        // A remote media track becomes available (camera, mic, screen-share)
        room.on("trackSubscribed", (track, publication, participant) => {
          this.attachRemoteTrack(track, participant);
          // Upsert in case this is the first signal we have for this participant
          this._upsertConnectedParticipant(participant);
        });

        // A remote media track goes away
        room.on("trackUnsubscribed", (track, publication, participant) => {
          this.detachRemoteTrack(track);
          if (participant?.sid) this.removeRemoteParticipantMedia(participant.sid);
        });

        // Host's own camera / screen-share published — attach to local preview elements
        room.on("localTrackPublished", (publication) => {
          this.$nextTick(() => {
            if (publication.source === Track.Source.Camera) {
              const videoEl = this.$refs.localVideoEl;
              if (videoEl && publication.track) publication.track.attach(videoEl);
            } else if (publication.source === Track.Source.ScreenShare) {
              const screenEl = this.$refs.screenShareEl;
              if (screenEl && publication.track) publication.track.attach(screenEl);
            }
          });
        });

        // Host stopped sharing screen via the browser's native UI
        room.on("localTrackUnpublished", (publication) => {
          if (publication.source === Track.Source.ScreenShare) {
            this.controlState.share = false;
            const screenEl = this.$refs.screenShareEl;
            if (screenEl && publication.track) publication.track.detach(screenEl);
          }
        });

        // Real-time data channel: chat messages + whiteboard draw packets
        room.on("dataReceived", (payload) => {
          try {
            const text = new TextDecoder().decode(payload);
            const packet = JSON.parse(text);

            if (packet.type === "draw") {
              const canvas = this.$refs.drawCanvas;
              if (canvas) {
                const ctx = canvas.getContext("2d");
                ctx.strokeStyle = packet.color || "#ff3b30";
                ctx.lineWidth = packet.width || 3;
                ctx.lineCap = "round";
                ctx.lineJoin = "round";
                ctx.beginPath();
                ctx.moveTo(packet.x0 * canvas.width, packet.y0 * canvas.height);
                ctx.lineTo(packet.x1 * canvas.width, packet.y1 * canvas.height);
                ctx.stroke();
              }
              return;
            }

            if (packet.type === "draw-clear") {
              const canvas = this.$refs.drawCanvas;
              if (canvas) {
                const ctx = canvas.getContext("2d");
                ctx.clearRect(0, 0, canvas.width, canvas.height);
              }
              return;
            }

            if (packet.type !== "chat") return;
            if (String(packet.userID) === String(this.currentAdminUserId)) return;

            const displayName = packet.name || `User #${packet.userID}`;
            const msgId = packet.id || `rt-${Date.now()}-${Math.random()}`;
            const newMsg = {
              id: msgId,
              sender: displayName,
              text: packet.message || "",
              time: this.formatDateTimeToTime(packet.createdAt || Date.now()),
              mine: false,
              replyTo: null,
            };

            if (this.chatSeenIds) this.chatSeenIds.add(String(msgId));
            const alreadyExists = this.chatMessages.some(
              (c) => c.id === newMsg.id || (c.text === newMsg.text && c.sender === newMsg.sender)
            );
            if (!alreadyExists) {
              this.chatMessages = [...this.chatMessages, newMsg];
              this.scrollChatToBottom();
            }
          } catch (_) {
            // Ignore non-chat or malformed data packets
          }
        });

        // ── Step 3: Connect ──────────────────────────────────────────────────
        await room.connect(url, token, { autoSubscribe: true });

        // ── Step 4: Assign room reference so computed props become valid ─────
        this.liveKitRoom = room;
        this.hostStats.watchingCount = Math.max(0, room.remoteParticipants.size);

        // ── Step 5: Seed the tile grid from participants already in the room ─
        // This is the essential step for breakout hops: learners who joined
        // before the host arrived are already in room.remoteParticipants but
        // will NOT trigger participantConnected or trackSubscribed going
        // forward. We must enumerate them here.
        room.remoteParticipants.forEach((participant) => {
          this._upsertConnectedParticipant(participant);
          participant.trackPublications.forEach((pub) => {
            if (pub.isSubscribed && pub.track) this.attachRemoteTrack(pub.track, participant);
          });
        });

        // ── Step 6: Publish host camera + mic ────────────────────────────────
        await room.localParticipant.setCameraEnabled(true);
        await room.localParticipant.setMicrophoneEnabled(true);

        // ── Step 7: StartLive — only on the very first connect ───────────────
        // sessionMeta.status is already "live" when re-entering or hopping
        // breakout rooms, so the POST is naturally skipped.
        const sessionAlreadyLive = this.sessionMeta.status === "live";
        if (!sessionAlreadyLive && this.startLiveEndpoint && this.currentAdminMemberId) {
          try {
            await this.$axios.post(this.startLiveEndpoint, null, {
              params: { memberId: this.currentAdminMemberId },
              headers: { accept: "*/*" },
            });
          } catch (_) {
            // Non-fatal: session may already be live
          }
        }

      } catch (error) {
        this.liveKitRoom = null;
        this.errorMessage = error.message || "Failed to connect to the live room.";
      } finally {
        this.isConnectingRoom = false;
      }
    },

    /** Backward-compatible wrapper kept for any existing call sites. */
    async connectToLiveRoom() {
      await this.connectRoom(this.liveKitUrl, this.roomToken);
    },

    disconnectLiveRoom() {
      this.stopChatPolling();
      this.stopPollResultsPolling(); // B01
      this.stopAutoRecordWatch(); // B03

      if (!this.liveKitRoom) {
        return;
      }

      this.liveKitRoom.disconnect();
      this.liveKitRoom = null;
      this.hostStats.watchingCount = 0;
      this.clearRemoteMedia();
    },

    attachRemoteTrack(track, participant) {
      if (!track || !participant) return;

      const participantSid = participant.sid;
      const trackSid = track.sid;

      // ── Audio: attach to hidden DOM sink ──────────────────────────────────
      if (track.kind === Track.Kind.Audio) {
        const audioSink = this.$refs.remoteAudioSink;
        if (!audioSink) return;

        let audioEl = audioSink.querySelector(`[data-track-sid="${trackSid}"]`);
        if (!audioEl) {
          audioEl = document.createElement("audio");
          audioEl.autoplay = true;
          audioEl.playsInline = true;
          audioEl.dataset.participantSid = participantSid;
          audioEl.dataset.trackSid = trackSid;
          audioSink.appendChild(audioEl);
        }
        track.attach(audioEl);
        const p = audioEl.play?.();
        if (p && typeof p.catch === "function") p.catch(() => {});
        return;
      }

      // ── Camera video: push to reactive array then attach after render ─────
      if (track.kind === Track.Kind.Video && track.source === Track.Source.Camera) {
        const exists = this.remoteVideoTracks.some((r) => r.trackSid === trackSid);
        if (exists) return;

        const name = participant.name || participant.identity || "Learner";
        this.remoteVideoTracks.push({ sid: participantSid, trackSid, name, track });

        this.$nextTick(() => {
          const refVal = this.$refs["rv_" + participantSid];
          const videoEl = Array.isArray(refVal) ? refVal[0] : refVal;
          if (videoEl) track.attach(videoEl);
        });
      }
    },

    detachRemoteTrack(track) {
      if (!track) return;

      const trackSid = track.sid;

      // Audio
      const audioSink = this.$refs.remoteAudioSink;
      const audioEl = audioSink?.querySelector(`[data-track-sid="${trackSid}"]`);
      if (audioEl) {
        track.detach(audioEl);
        audioEl.remove();
      }

      // Video: remove from reactive array
      this.remoteVideoTracks = this.remoteVideoTracks.filter((r) => r.trackSid !== trackSid);
    },

    removeRemoteParticipantMedia(participantSid) {
      if (!participantSid) return;

      // Audio
      const audioSink = this.$refs.remoteAudioSink;
      audioSink
        ?.querySelectorAll(`[data-participant-sid="${participantSid}"]`)
        .forEach((el) => el.remove());

      // Video: remove from reactive array
      this.remoteVideoTracks = this.remoteVideoTracks.filter((r) => r.sid !== participantSid);
    },

    clearRemoteMedia() {
      const audioSink = this.$refs.remoteAudioSink;
      if (audioSink) audioSink.innerHTML = "";
      this.remoteVideoTracks = [];
    },

    syncRemoteParticipantTracks() {
      if (!this.liveKitRoom) return;

      this.liveKitRoom.remoteParticipants.forEach((participant) => {
        participant.trackPublications.forEach((publication) => {
          if (publication.track) {
            this.attachRemoteTrack(publication.track, participant);
          }
        });
      });
    },

    // Rebuild the full reactive connected-learner list from the current room.
    // Called after fetchHostParticipants loads (to re-resolve display names)
    // and when the breakout dialog opens. Per-participant incremental updates
    // during events use _upsertConnectedParticipant instead.
    syncConnectedParticipants() {
      if (!this.liveKitRoom || !this.liveKitRoom.remoteParticipants) {
        this.connectedParticipants = [];
        return;
      }
      // Rebuild from scratch so stale entries are removed.
      this.connectedParticipants = [];
      this.liveKitRoom.remoteParticipants.forEach((p) => {
        this._upsertConnectedParticipant(p);
      });
    },

    /**
     * Insert or refresh a single participant entry in the reactive
     * `connectedParticipants` array.  Name resolution checks the enrolled-
     * participant list first (resolved full names) then the raw hostUsers
     * employee list, so we never display a bare numeric identity.
     *
     * Uses array replacement (not .push) so Vue 2 reactivity fires and the
     * Google-Meet grid computed properties recalculate immediately.
     */
    _upsertConnectedParticipant(p) {
      const userID = parseInt(p.identity, 10) || 0;

      const enrolled = this.participants.find((ep) => ep.userID === userID);
      const hostUser = !enrolled && this.hostUsers
        ? this.hostUsers.find((u) => parseInt(u.userID || u.id || u.employeeID, 10) === userID)
        : null;

      const name =
        enrolled?.name ||
        hostUser?.fullName ||
        hostUser?.name ||
        hostUser?.employeeName ||
        (p.name && !/^\d+$/.test(p.name) ? p.name : null) ||
        `Participant ${userID || p.sid}`;

      const entry = { id: p.sid, userID, name, initials: this.buildInitials(name) };

      const idx = this.connectedParticipants.findIndex((cp) => cp.id === p.sid);
      if (idx === -1) {
        // Replace array reference so Vue 2 detects the change
        this.connectedParticipants = [...this.connectedParticipants, entry];
      } else {
        // Update in-place via $set to trigger reactivity on the existing slot
        this.$set(this.connectedParticipants, idx, entry);
      }
    },

    async handleAdminEnterHostRoom() {
      if (!this.currentSessionId) {
        return;
      }

      if (!this.currentAdminMemberId || !this.currentAdminUserId) {
        this.errorMessage = this.$t("livefeature_admin_missing");
        return;
      }

      this.isLoading = true;
      this.errorMessage = "";

      try {
        const tokenResponse = await this.$axios.post(
          this.getRoomTokenEndpoint,
          {
            sessionID: this.currentSessionId,
            userID: this.currentAdminUserId,
            userName: this.currentAdminUserName,
          },
          {
            headers: {
              accept: "*/*",
              "Content-Type": "application/json",
            },
          }
        );

        if (tokenResponse.data?.status !== 0) {
          throw new Error(tokenResponse.data?.message || this.$t("livefeature_failed_room_token"));
        }

        this.roomToken = tokenResponse.data?.data?.token || "";
        this.liveKitUrl = tokenResponse.data?.data?.liveKitUrl || "";
        this.roomId = tokenResponse.data?.data?.roomID || "";

        // Only stamp the timer start time on first entry; preserve it on re-entry
        if (!this.getPersistedLiveTimerStartTime()) {
          this.persistLiveTimerStartTime(new Date().toISOString());
        }
        this.startSessionTimer(this.getPersistedLiveTimerStartTime());

        // Fetch recordings immediately after room token is obtained
        await this.fetchSessionRecordings();
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message ||
          error.message ||
          this.$t("livefeature_enter_host_failed");
      } finally {
        this.isLoading = false;
      }
    },

    async fetchHostSessionContext() {
      this.infoMessage = "";

      const routeQuery = this.$route.query || {};
      this.sessionMeta = {
        ...this.sessionMeta,
        title: routeQuery.title || this.sessionMeta.title,
        subtitle: routeQuery.status ? `${this.$t("livefeature_live")} / ${routeQuery.status}` : this.sessionMeta.subtitle,
        statusLabel: routeQuery.status || this.sessionMeta.statusLabel,
        shareTitle: routeQuery.title
          ? `${this.$t("livefeature_ready_to_host")}: ${routeQuery.title}`
          : this.sessionMeta.shareTitle,
        shareSubtitle: routeQuery.duration || this.sessionMeta.shareSubtitle,
        hostName: routeQuery.host || this.sessionMeta.hostName,
      };

      if (!this.sessionDetailEndpoint) {
        return;
      }

      try {
        const response = await this.$axios.get(this.sessionDetailEndpoint, {
          headers: {
            accept: "*/*",
          },
        });

        const session = response.data?.data || response.data;

        if (!session || typeof session !== "object") {
          return;
        }

        this.sessionMeta = {
          ...this.sessionMeta,
          title: session.title || session.liveSessionTitle || this.sessionMeta.title,
          subtitle: this.$t("livefeature_live_host_studio"),
          statusLabel: session.statusName || session.statusLabel || this.normalizeStatusLabel(session.status),
          status: this.normalizeStatus(session.status),
          autoRecord: Boolean(session.autoRecord ?? session.AutoRecord),
          startTime: session.startTime || "",
          endTime: session.endTime || "",
          shareTitle: session.title
            ? `${this.$t("livefeature_ready_to_host")}: ${session.title}`
            : this.sessionMeta.shareTitle,
          shareSubtitle:
            this.sessionMeta.shareSubtitle || this.buildSessionTimeLabel(session.startTime, session.endTime),
          hostName: session.hostUserName || session.hostName || this.sessionMeta.hostName,
        };

        if (this.sessionMeta.status === "live") {
          const liveStartedAt = this.resolveLiveTimerStartTime(session);

          if (liveStartedAt) {
            this.persistLiveTimerStartTime(liveStartedAt);
          }

          this.startSessionTimer(this.getPersistedLiveTimerStartTime() || liveStartedAt);
        } else {
          this.clearPersistedLiveTimerStartTime();
          this.stopSessionTimer();
        }
      } catch (error) {
        this.infoMessage = this.$t("livefeature_host_context_fallback");
      }
    },

    async fetchHostParticipants() {
      if (!this.currentSessionId) {
        return;
      }

      this.participantLoading = true;

      try {
        const companyID = localStorage.getItem("companyID") || "";
        const [participantResponse, userResponse] = await Promise.all([
          this.$axios.get(
            `${this.web_url}LiveSession/GetAllParticipantsLiveSession/${this.currentSessionId}`,
            {
              headers: {
                accept: "*/*",
              },
            }
          ),
          this.$axios.get(
            `${this.web_urlV6}EmployeeEXT/GetEmployeeExtAllV4?id=${companyID}`,
            {
              headers: {
                accept: "*/*",
              },
            }
          ),
        ]);

        const enrollments = Array.isArray(participantResponse.data?.data)
          ? participantResponse.data.data
          : Array.isArray(participantResponse.data)
          ? participantResponse.data
          : [];

        const users = Array.isArray(userResponse.data?.data) ? userResponse.data.data : [];
        this.hostUsers = users;

        this.participants = enrollments.map((participant, index) => {
          const matchedUser = users.find((user) => {
            const candidateValues = [user.userID, user.employeeID, user.id]
              .map((value) => String(value || "").trim())
              .filter(Boolean);

            return candidateValues.includes(String(participant.userID || participant.UserID || "").trim());
          });

          const fullName = matchedUser?.fullName || matchedUser?.name || matchedUser?.employeeName || `${this.$t("livefeature_participants_label")} ${index + 1}`;
          const status = this.buildParticipantStatus(participant);

          return {
            id: participant.id || participant.liveEnrollID || index + 1,
            userID: parseInt(participant.userID || participant.UserID || "0", 10),
            initials: this.buildInitials(fullName),
            name: fullName,
            role: matchedUser?.jobTitle || matchedUser?.departmentName || this.$t("livefeature_failed_role_learner"),
            badge: status.label,
            badgeColor: status.badgeColor,
            badgeText: status.badgeText,
            color: this.pickParticipantColor(index),
          };
        });

        this.hostStats.watchingCount = this.participants.length;
      } catch (error) {
        this.infoMessage = this.$t("livefeature_participants_panel_ready");
        this.participants = [];
      } finally {
        this.participantLoading = false;
        // Now that the enrolled participant names are available, re-resolve the
        // display names for anyone already connected to the live room.
        this.syncConnectedParticipants();
      }
    },

    async fetchSessionRecordings() {
      if (!this.sessionRecordingsEndpoint) {
        return;
      }

      // B03 (F2/F4/E48) — ใช้กำหนด isRecording อย่างเดียว · ไม่เขียนรายการ/สถานะโหลดของแท็บบันทึก
      // (รายการบันทึกมาจาก GetRecordingLibraryStatus — กันแท็บกระพริบระหว่าง poll auto-record)
      try {
        const response = await this.$axios.get(this.sessionRecordingsEndpoint, {
          headers: { accept: "*/*" },
        });

        // Inverted mapping per team lead requirement:
        // data === true  → recording NOT yet active → isRecording = false → "Start Recording"
        // data === false → recording IS active      → isRecording = true  → "Stop Recording"
        if (response.data?.status === 0 && typeof response.data?.data === "boolean") {
          this.isRecording = !response.data.data;
        }
      } catch (error) {
        // B03 — คงค่า isRecording เดิมเมื่อเรียกไม่สำเร็จ
      }
    },

    // B03 (F4/D28) — รายการบันทึก + สถานะไลบรารี่ (อ่านอย่างเดียว) · ล้มไม่ตั้ง errorMessage
    async fetchRecordingLibraryStatus({ silent = false } = {}) {
      if (!this.recordingLibraryStatusEndpoint) return;
      this.recordingsLoading = !silent && !this.recordingsLoadedOnce;
      try {
        const response = await this.$axios.get(this.recordingLibraryStatusEndpoint, {
          headers: { accept: "*/*" },
        });
        if (response.data?.status !== 0) throw new Error("GetRecordingLibraryStatus failed");
        this.recordings = Array.isArray(response.data.data) ? response.data.data : [];
        this.recordingsLoadedOnce = true;
        this.recordingsLoadError = false;
        if (this.recordingsTabActive && this.hasProcessingRecording) this.startRecordingsRefresh();
      } catch (error) {
        this.recordingsLoadError = true; // template แสดง alert เฉพาะเมื่อยังไม่เคยโหลดสำเร็จ
        console.warn("[Host] GetRecordingLibraryStatus:", error.response?.status, error.message);
      } finally {
        this.recordingsLoading = false;
      }
    },

    // B03 (F2/D24, UX-Q2) — เฝ้าการเริ่ม auto-record ทุก 5 วิ ≤ 13 ครั้ง (65 วิ)
    watchAutoRecordStart() {
      if (this._isBeingDestroyed || this._isDestroyed) return; // created() async — แบบ B01
      if (this.autoRecordWatchTimer) return;
      if (!this.liveKitRoom || !this.sessionMeta.autoRecord || this.sessionMeta.status !== "live" || this.isRecording) return;
      // session เคยบันทึกเสร็จ/ล้มแล้ว → backend ไม่เริ่มเอง (D13 ข้อ 2) · นับทุกแถวรวม eligible === false (D31)
      if (this.recordings.some((r) => r.recordStatus === 1 || r.recordStatus === 2)) return;

      this.autoRecordWatchState = "starting";
      this.autoRecordWatchCount = 0;
      this.autoRecordPollBusy = false;
      const timer = setInterval(async () => {
        if (this._isBeingDestroyed || this._isDestroyed) {
          clearInterval(timer);
          return;
        }
        this.autoRecordWatchCount += 1;
        if (!this.autoRecordPollBusy) {
          this.autoRecordPollBusy = true;
          try {
            await this.fetchSessionRecordings();
          } finally {
            this.autoRecordPollBusy = false;
          }
          if (this._isBeingDestroyed || this._isDestroyed) return;
          // เช็ก isRecording ก่อนเช็ก timer: watch.isRecording (flush ก่อน continuation นี้) อาจหยุด timer ไปแล้ว (Plan R1)
          if (this.isRecording) {
            this.stopAutoRecordWatch();
            this.fetchRecordingLibraryStatus({ silent: true });
            return;
          }
          if (this.autoRecordWatchTimer !== timer) return; // ถูกหยุดระหว่างรอ (End/Start เอง/ออกหน้า)
        }
        if (this.autoRecordWatchCount >= 13 && this.autoRecordWatchTimer === timer) {
          clearInterval(timer);
          this.autoRecordWatchTimer = null;
          this.autoRecordWatchState = "not_started"; // ค้างจนเริ่มอัด/ออกจากหน้า
        }
      }, 5000);
      this.autoRecordWatchTimer = timer;
    },

    stopAutoRecordWatch() {
      if (this.autoRecordWatchTimer) clearInterval(this.autoRecordWatchTimer);
      this.autoRecordWatchTimer = null;
      this.autoRecordWatchState = "idle";
    },

    // B03 (F4) — refresh เงียบทุก 30 วิ ≤ 20 ครั้ง เฉพาะเมื่อมีแถวกำลังประมวลผล
    startRecordingsRefresh() {
      if (this._isBeingDestroyed || this._isDestroyed) return;
      if (this.recordingsRefreshTimer) return;
      this.recordingsRefreshCount = 0;
      this.recordingsRefreshTimer = setInterval(() => {
        if (this._isBeingDestroyed || this._isDestroyed) {
          this.stopRecordingsRefresh();
          return;
        }
        if (this.isRetryingImport) return; // ไม่ทับผลของปุ่มลองใหม่ (Plan C8)
        if (!this.hasProcessingRecording || this.recordingsRefreshCount >= 20) {
          this.stopRecordingsRefresh();
          return;
        }
        this.recordingsRefreshCount += 1;
        this.fetchRecordingLibraryStatus({ silent: true });
      }, 30000);
    },

    stopRecordingsRefresh() {
      if (this.recordingsRefreshTimer) clearInterval(this.recordingsRefreshTimer);
      this.recordingsRefreshTimer = null;
    },

    // B03 (D26) — POST ทั้ง session ≤ 60 วิ (K19) · กันกดซ้ำ
    async retryRecordingImport(rec) {
      if (this.isRetryingImport || !rec || !this.recordingImportEndpoint) return;
      this.isRetryingImport = true;
      this.retryingRecordId = rec.liveSessionRecordID;
      this.errorMessage = "";
      try {
        const response = await this.$axios.post(this.recordingImportEndpoint, null, {
          headers: { accept: "*/*" },
        });
        if (response.data?.status !== 0) throw new Error("ImportRecordingsToLibrary failed");
        this.recordings = Array.isArray(response.data.data) ? response.data.data : [];
        this.recordingsLoadedOnce = true;
        this.recordingsLoadError = false;
        const same = this.recordings.find((r) => r.liveSessionRecordID === rec.liveSessionRecordID);
        if (same && same.libraryStatus === 3) this.infoMessage = this.$t("livefeature_recording_retry_still_failed");
        if (this.recordingsTabActive && this.hasProcessingRecording) this.startRecordingsRefresh();
      } catch (error) {
        console.warn("[Host] ImportRecordingsToLibrary:", error.response?.status, error.message);
        this.infoMessage = "";
        this.errorMessage = error.response?.status === 403
          ? this.$t("livefeature_recording_no_permission")
          : this.$t("livefeature_recording_retry_failed");
      } finally {
        this.isRetryingImport = false;
        this.retryingRecordId = "";
      }
    },

    formatRecordingDate(value) {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    },

    async fetchSessionPolls() {
      if (!this.sessionPollsEndpoint) {
        this.pollLoading = false; // B01 — กัน "กำลังโหลด" ค้างเมื่อไม่มี roomId
        return;
      }

      this.pollLoading = true;

      try {
        const response = await this.$axios.get(this.sessionPollsEndpoint, {
          headers: {
            accept: "*/*",
          },
        });

        const polls = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
          ? response.data
          : [];

        if (!polls.length) {
          this.activePoll = null;
          return;
        }

        const latestPoll = [...polls].sort((leftPoll, rightPoll) => {
          const leftDate = new Date(leftPoll.createdDate || leftPoll.CreatedDate || 0).getTime();
          const rightDate = new Date(rightPoll.createdDate || rightPoll.CreatedDate || 0).getTime();
          return rightDate - leftDate;
        })[0];

        const latestPollId = latestPoll.id || latestPoll.ID;

        if (!latestPollId) {
          this.activePoll = this.buildPollViewModel(latestPoll);
          this.showPoll = Boolean(this.activePoll);
          return;
        }

        await this.fetchPollResults(latestPollId, latestPoll);
      } catch (error) {
        this.activePoll = this.activePoll || null;
      } finally {
        this.pollLoading = false;
      }
    },

    async fetchPollResults(pollId, pollMeta = null) {
      if (!pollId) {
        return;
      }

      try {
        const response = await this.$axios.get(`${this.web_url}LiveSession/GetPollResults/${pollId}`, {
          headers: {
            accept: "*/*",
          },
        });

        const result = response.data?.data || response.data;

        if (!result) {
          this.activePoll = this.buildPollViewModel({ ...(pollMeta || {}), id: pollId });
          this.showPoll = Boolean(this.activePoll);
          return;
        }

        this.activePoll = this.buildPollViewModel(
          {
            ...(pollMeta || {}),
            ...(result || {}),
            id: result.pollID || result.PollID || pollId,
          },
          result.options || result.Options
        );

        this.showPoll = true;
      } catch (error) {
        this.activePoll = this.buildPollViewModel({ ...(pollMeta || {}), id: pollId });
        this.showPoll = Boolean(this.activePoll);
        this.infoMessage = this.$t("livefeature_poll_results_unavailable");
      }
    },

    buildPollViewModel(pollData = {}, optionSource = null) {
      if (!pollData || typeof pollData !== "object") {
        return null;
      }

      const pollId = pollData.id || pollData.ID || pollData.pollID || pollData.PollID || "";
      let options = this.buildPollOptions(optionSource || pollData.options || pollData.Options || []);

      if (!options.length && this.activePoll && String(this.activePoll.id || "") === String(pollId || "")) {
        options = Array.isArray(this.activePoll.options) ? this.activePoll.options : [];
      }

      return {
        id: pollId || `poll-${Date.now()}`,
        title: this.$t("livefeature_currently_open_poll"),
        statusLabel: `● ${this.$t("livefeature_live_badge")}`,
        question: pollData.question || pollData.Question || "Poll",
        options,
      };
    },

    buildPollOptions(optionSource = []) {
      const options = Array.isArray(optionSource) ? optionSource : [];

      return options.map((option, index) => ({
        id: option.id || option.ID || option.optionID || option.OptionID || index + 1,
        optionText:
          option.optionText ||
          option.OptionText ||
          option.label ||
          `${this.$t("livefeature_option")} ${index + 1}`,
        voteCount: option.voteCount || option.VoteCount || 0,
        percentage: option.percentage || option.Percentage || 0,
        optionImage: option.optionImage || option.OptionImage || "", // B01 — URL รูป (SAS จาก API)
        primary: index === 0,
      }));
    },

    async fetchSessionChat() {
      if (!this.sessionChatEndpoint) {
        return;
      }

      this.chatLoading = true;

      try {
        const companyID = localStorage.getItem("companyID") || "";
        const [chatResponse, userResponse] = await Promise.all([
          this.$axios.get(this.sessionChatEndpoint, {
            headers: {
              accept: "*/*",
            },
          }),
          this.hostUsers.length
            ? Promise.resolve({ data: { data: this.hostUsers } })
            : this.$axios.get(`${this.web_urlV6}EmployeeEXT/GetEmployeeExtAllV4?id=${companyID}`, {
                headers: {
                  accept: "*/*",
                },
              }),
        ]);

        const chats = Array.isArray(chatResponse.data?.data)
          ? chatResponse.data.data
          : Array.isArray(chatResponse.data)
          ? chatResponse.data
          : [];

        const users = Array.isArray(userResponse.data?.data)
          ? userResponse.data.data
          : [];

        if (users.length) {
          this.hostUsers = users;
        }

        // Snapshot any replyTo data that exists only in local state (not persisted by API).
        // Key: String(id) — stable server IDs only.
        const localReplyMap = this.chatMessages.reduce((map, c) => {
          const replyTo =
            c.replyTo ||
            (c.replyToName ? { name: c.replyToName, message: c.replyToMessage || "" } : null);
          if (!replyTo) return map;
          map[String(c.id)] = replyTo;
          return map;
        }, {});

        // Set of ALL IDs that existed before this fetch (including optimistic "local-…" IDs).
        // Used in _normalizeReplyFields to restrict queue lookups to genuinely new messages
        // so that old messages with identical text never consume a pending reply entry.
        const prevIdSet = new Set(this.chatMessages.map((c) => String(c.id)));

        this.chatMessages = chats
          .map((chat, index) => {
            const chatUserId = chat.userID ?? chat.UserID;
            const matchedUser = this.hostUsers.find((user) => {
              const candidateValues = [user.userID, user.employeeID, user.id]
                .map((value) => String(value || "").trim())
                .filter(Boolean);

              return candidateValues.includes(String(chatUserId || "").trim());
            });

            const displayName =
              matchedUser?.fullName ||
              matchedUser?.name ||
              (Number(chatUserId) === Number(this.currentAdminUserId)
                ? this.currentAdminUserName || "Host"
                : `User #${chatUserId || index + 1}`);

            return {
              id: chat.id || chat.ID || index + 1,
              apiOrder: index,
              initials: this.buildInitials(displayName),
              name: displayName,
              createdAt: new Date(chat.createdDate || chat.CreatedDate || 0).getTime(),
              message: chat.message || chat.Message || "",
              color: Number(chatUserId) === Number(this.currentAdminUserId) ? "#dcfce7" : "#dbeafe",
              isMine: Number(chatUserId) === Number(this.currentAdminUserId),
              // ── Reply normalization ──────────────────────────────────────────
              // Priority 1: API returns explicit reply fields (future-proof).
              // Priority 2: Carry forward from local state snapshot (current path).
              ...this._normalizeReplyFields(chat, String(chat.id || chat.ID || index + 1), localReplyMap, prevIdSet),
              pending: false,
            };
          })
          .sort((leftChat, rightChat) => {
            const leftCreatedAt = Number.isFinite(leftChat.createdAt) ? leftChat.createdAt : 0;
            const rightCreatedAt = Number.isFinite(rightChat.createdAt) ? rightChat.createdAt : 0;

            if (leftCreatedAt === rightCreatedAt) {
              return rightChat.apiOrder - leftChat.apiOrder;
            }

            return leftCreatedAt - rightCreatedAt;
          });

        // Only scroll to bottom if already near the bottom, so polling doesn't
        // interrupt a user who has scrolled up to read older messages.
        const chatPanel = this.$refs.chatPanel;
        const wasNearBottom = !chatPanel ||
          chatPanel.scrollHeight - chatPanel.scrollTop - chatPanel.clientHeight < 80;
        if (wasNearBottom) this.scrollChatToBottom();
      } catch (error) {
        this.infoMessage = this.$t("livefeature_chat_unavailable");
      } finally {
        this.chatLoading = false;
      }
    },

    async handleControlClick(control) {
      switch (control.action) {
        case "create-poll":
          this.openPollDialog(); // B01 — ล้าง pollDialogError ตอนเปิด
          break;
        case "people":
          this.activeTab = 1;
          break;
        case "breakout-rooms":
          // Auto-fetch participants if not already loaded
          if (!this.participants.length && !this.participantLoading) {
            await this.fetchHostParticipants();
          }
          // Refresh the live connected list so names/attendees are current
          this.syncConnectedParticipants();
          this.showBreakoutDialog = true;
          break;
        case "mic":
          if (this.liveKitRoom) {
            const micEnabled = !this.controlState.mic;
            await this.liveKitRoom.localParticipant.setMicrophoneEnabled(micEnabled);
          }
          this.controlState.mic = !this.controlState.mic;
          this.infoMessage = `Mic ${this.controlState.mic ? this.$t("livefeature_control_on") : this.$t("livefeature_control_off")}`;
          break;
        case "camera":
          if (this.liveKitRoom) {
            const cameraEnabled = !this.controlState.camera;
            await this.liveKitRoom.localParticipant.setCameraEnabled(cameraEnabled);
          }
          this.controlState.camera = !this.controlState.camera;
          this.infoMessage = `Camera ${this.controlState.camera ? this.$t("livefeature_control_on") : this.$t("livefeature_control_off")}`;
          break;
        case "share": {
          const shareEnabled = !this.controlState.share;
          if (this.liveKitRoom) {
            if (shareEnabled) {
              await this.liveKitRoom.localParticipant.setScreenShareEnabled(true, {
                audio: true,
                video: { displaySurface: "monitor" },
              });
            } else {
              await this.liveKitRoom.localParticipant.setScreenShareEnabled(false);
            }
          }
          // Update state so Vue shows/hides the video element
          this.controlState.share = shareEnabled;
          this.infoMessage = `Screen share ${shareEnabled ? this.$t("livefeature_control_on") : this.$t("livefeature_control_off")}`;

          if (shareEnabled) {
            // Wait for Vue to render the visible <video> element, then attach the track
            await this.$nextTick();
            const screenEl = this.$refs.screenShareEl;
            if (screenEl && this.liveKitRoom) {
              const publications = this.liveKitRoom.localParticipant.trackPublications;
              for (const [, pub] of publications) {
                if (pub.source === Track.Source.ScreenShare && pub.track) {
                  pub.track.attach(screenEl);
                  break;
                }
              }
            }
          } else {
            // Detach cleanly when stopping
            const screenEl = this.$refs.screenShareEl;
            if (screenEl) {
              screenEl.srcObject = null;
            }
          }
          break;
        }
        case "settings":
          this.infoMessage = this.$t("livefeature_settings_ready");
          this.controlState.settings = !this.controlState.settings;
          break;
        case "draw": {
          const drawEnabled = !this.controlState.draw;
          this.controlState.draw = drawEnabled;
          this.infoMessage = drawEnabled ? "Draw mode on" : "Draw mode off";
          if (drawEnabled) {
            this.$nextTick(() => {
              const canvas = this.$refs.drawCanvas;
              if (canvas) {
                canvas.width = canvas.offsetWidth;
                canvas.height = canvas.offsetHeight;
              }
              const embedCanvas = this.$refs.embedDrawCanvas;
              if (embedCanvas) {
                embedCanvas.width = embedCanvas.offsetWidth;
                embedCanvas.height = embedCanvas.offsetHeight;
              }
            });
          } else {
            this.isDrawing = false;
            this.$nextTick(() => {
              const canvas = this.$refs.drawCanvas;
              if (canvas) {
                const ctx = canvas.getContext("2d");
                ctx.clearRect(0, 0, canvas.width, canvas.height);
              }
            });
            // Broadcast clear to remote participants
            if (this.liveKitRoom) {
              try {
                const packet = JSON.stringify({ type: "draw-clear" });
                this.liveKitRoom.localParticipant.publishData(
                  (this.chatEncoder || new TextEncoder()).encode(packet),
                  { reliable: true }
                );
              } catch (_) {
                // Non-fatal
              }
            }
          }
          break;
        }
        case "mute-all":
          await this.muteAllParticipants();
          break;
        default:
          this.controlState[control.action] = !this.controlState[control.action];
          this.infoMessage = `${control.label} ${this.controlState[control.action] ? this.$t("livefeature_control_on") : this.$t("livefeature_control_off")}`;
      }
    },

    async muteAllParticipants() {
      if (this.muteAllLoading) return;

      if (!this.currentSessionId) {
        this.errorMessage = "Live session ID is missing.";
        return;
      }

      this.muteAllLoading = true;
      this.errorMessage = "";

      try {
        const response = await this.$axios.post(
          this.muteAllEndpoint,
          {
            SessionID: this.currentSessionId,
            HostUserID: this.currentAdminUserId,
            Mute: true,
          },
          {
            headers: {
              accept: "*/*",
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status !== 200) {
          throw new Error("Failed to mute all participants.");
        }

        const apiStatus = response.data?.status;
        if (typeof apiStatus !== "undefined" && apiStatus !== 0) {
          throw new Error(response.data?.message || "Failed to mute all participants.");
        }

        this.controlState["mute-all"] = true;
        this.infoMessage = "All participants have been muted successfully";
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to mute all participants. Please try again.";
      } finally {
        this.muteAllLoading = false;
      }
    },

    // Manual breakout assignment helpers
    assignParticipant(participantId, roomIndex) {
      this.$set(this.manualRoomAssignments, participantId, roomIndex);
    },

    unassignParticipant(participantId) {
      this.$delete(this.manualRoomAssignments, participantId);
    },

    getParticipantsForRoom(roomIndex) {
      return this.availableParticipants.filter(
        (p) => this.manualRoomAssignments[p.id] === roomIndex
      );
    },

    getRoomParticipantCount(roomIndex) {
      return this.getParticipantsForRoom(roomIndex).length;
    },

    // Restore active breakout rooms from the backend after a page reload.
    // Keeps the host in "Manage Breakout Rooms" mode if rooms are still active.
    async restoreBreakoutRooms() {
      if (!this.getBreakoutRoomsEndpoint) return;
      try {
        const res = await this.$axios.get(this.getBreakoutRoomsEndpoint, {
          headers: { accept: "*/*" },
        });
        const allRooms = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
          ? res.data
          : [];

        // Only keep rooms that are still active (ignore ended/closed ones)
        const activeRooms = allRooms.filter((r) => {
          if (r.status === undefined || r.status === null) return true;
          const s = String(r.status).toLowerCase();
          return !(Number(r.status) === 2 || s === "ended" || s === "closed" || s === "inactive");
        });

        if (!activeRooms.length) {
          this.isInBreakoutRoom = false;
          this.breakoutRooms = [];
          return;
        }

        this.breakoutRooms = activeRooms.map((room, idx) => ({
          ...room,
          roomName: room.roomName || room.RoomName || `Breakout Room ${idx + 1}`,
          participantCount: Array.isArray(room.participants) ? room.participants.length : 0,
          assignedUserIds: Array.isArray(room.participants)
            ? room.participants.map((p) => p.userID || p.UserID || p.userId).filter(Boolean)
            : [],
        }));
        this.isInBreakoutRoom = true;
      } catch (_) {
        // Non-fatal — if restore fails, host simply sees the create form
      }
    },

    async createBreakoutRooms() {
      if (this.breakoutLoading || !this.currentSessionId) return;

      this.breakoutLoading = true;
      this.errorMessage = "";

      try {
        // ── Build participant distribution ──────────────────────────
        const roomCount = this.breakoutRoomCount || 2;
        let groups;

        if (this.showManualAssignment) {
          // Manual assignment mode: use manualRoomAssignments
          groups = Array.from({ length: roomCount }, (_, i) => {
            const assignedUserIds = this.availableParticipants
              .filter((p) => this.manualRoomAssignments[p.id] === i)
              .map((p) => p.userID);

            return {
              RoomName: `Breakout Room ${i + 1}`,
              GroupIndex: i + 1,
              UserIds: assignedUserIds,
            };
          });
        } else {
          // Auto-assignment mode: equal distribution
          // Prefer live LiveKit identities (only currently-connected learners).
          // Fall back to this.participants (enrolled list) if no one is connected yet.
          const liveIds = this.liveKitRoom
            ? Array.from(this.liveKitRoom.remoteParticipants.values())
                .map((p) => parseInt(p.identity, 10))
                .filter((id) => !Number.isNaN(id) && id > 0)
            : [];

          const learnerIds = liveIds.length
            ? liveIds
            : this.participants
                .map((p) => p.userID)
                .filter((id) => id && id > 0);

          // Validate we have participants to assign
          if (!learnerIds.length) {
            throw new Error(
              "No participants available to assign. " +
              (this.liveKitRoom
                ? "Wait for learners to join the session first."
                : "Fetch session participants from the Participants tab first.")
            );
          }

          const chunkSize = Math.ceil(learnerIds.length / roomCount);

          // Divide learners into equal-sized groups
          groups = Array.from({ length: roomCount }, (_, i) => ({
            RoomName: `Breakout Room ${i + 1}`,
            GroupIndex: i + 1,
            UserIds: learnerIds.slice(i * chunkSize, (i + 1) * chunkSize),
          }));
        }

        const response = await this.$axios.post(
          this.createBreakoutRoomsEndpoint,
          {
            SessionID: this.currentSessionId,
            NumberOfRooms: roomCount,
            HostUserID: this.currentAdminUserId,
            Groups: groups,
          },
          { headers: { accept: "*/*", "Content-Type": "application/json" } }
        );

        const rooms = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
          ? response.data
          : [];

        if (!rooms.length) {
          throw new Error("No breakout rooms were created. Please try again.");
        }

        // Enrich rooms with participant counts from our local Groups data
        this.breakoutRooms = rooms.map((room, idx) => ({
          ...room,
          participantCount: groups[idx]?.UserIds?.length || 0,
          assignedUserIds: groups[idx]?.UserIds || [],
        }));
        
        this.isInBreakoutRoom = true;

        // Count total assigned participants across all rooms
        let totalAssigned = 0;
        groups.forEach(g => {
          totalAssigned += (g.UserIds?.length || 0);
        });

        this.infoMessage = `${rooms.length} breakout rooms created. ${totalAssigned} participant(s) assigned.`;

        // ── Broadcast per-user breakout-assigned packets ────────────
        // Send one packet per assigned participant so each learner's
        // Viewer.vue receives its specific roomID, regardless of whether
        // the backend returned populated participants arrays.
        if (this.liveKitRoom) {
          let broadcastCount = 0;
          
          rooms.forEach((room, roomIndex) => {
            const roomId   = room.breakoutRoomID || room.id || room.ID || room.roomID;
            const roomName = room.roomName || room.RoomName || `Breakout Room ${roomIndex + 1}`;

            // Use server-returned participants if available, else fall back to
            // the locally-computed group for this index (UserIds is now a primitive integer array).
            const memberList = Array.isArray(room.participants) && room.participants.length
              ? room.participants
              : (groups[roomIndex]?.UserIds || []);

            console.log(`[Breakout] Room ${roomIndex + 1} (${roomName}):`, {
              roomId,
              backendParticipants: room.participants,
              localUserIds: groups[roomIndex]?.UserIds,
              usingFallback: !(Array.isArray(room.participants) && room.participants.length),
              memberListCount: memberList.length
            });

            memberList.forEach((member) => {
              // member is either a primitive integer (from UserIds) or an object (from backend participants)
              const uid = typeof member === 'number' ? member : (member.userID || member.UserID || member.id);
              if (!uid) return;
              
              try {
                const packet = JSON.stringify({
                  type: "breakout-assigned",
                  userID: uid,
                  roomID: roomId,
                  roomName,
                });
                
                console.log(`[Breakout] Broadcasting invitation to UserID ${uid} for ${roomName}`, packet);
                
                this.liveKitRoom.localParticipant.publishData(
                  new TextEncoder().encode(packet),
                  { reliable: true }
                );
                broadcastCount++;
              } catch (err) {
                console.error(`[Breakout] Failed to broadcast to UserID ${uid}:`, err);
              }
            });
          });

          console.log(`[Breakout] Sent ${broadcastCount} invitation(s) to participants`);
        }
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to create breakout rooms.";
      } finally {
        this.breakoutLoading = false;
      }
    },

    async joinBreakoutRoom(room) {
      if (this.breakoutLoading || !room) return;

      this.breakoutLoading = true;
      this.errorMessage = "";

      try {
        const breakoutRoomId =
          room.breakoutRoomID || room.id || room.ID || room.roomID;

        if (!breakoutRoomId) {
          throw new Error("Breakout room ID is missing.");
        }

        const tokenResponse = await this.$axios.post(
          this.getBreakoutRoomTokenEndpoint,
          {
            BreakoutRoomID: breakoutRoomId,
            UserID: this.currentAdminUserId,
            UserName: this.currentAdminUserName,
          },
          { headers: { accept: "*/*", "Content-Type": "application/json" } }
        );

        const token = tokenResponse.data?.data?.token || tokenResponse.data?.token || "";
        const liveKitUrl = tokenResponse.data?.data?.liveKitUrl || this.liveKitUrl;

        if (!token) throw new Error("Could not obtain breakout room token.");

        // Save main-room credentials so we can return later
        if (!this.mainRoomToken) {
          this.mainRoomToken = this.roomToken;
          this.mainLiveKitUrl = this.liveKitUrl;
        }

        // Update reactive refs used by other computed endpoints
        this.roomToken = token;
        this.liveKitUrl = liveKitUrl;
        this.currentBreakoutRoomId = breakoutRoomId;

        // connectRoom handles full teardown + event re-binding internally
        await this.connectRoom(liveKitUrl, token);
        this.infoMessage = `Monitoring "${room.roomName || "Breakout Room"}"`;
        this.showBreakoutDialog = false;
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to join breakout room.";
      } finally {
        this.breakoutLoading = false;
      }
    },

    async returnToMainRoom() {
      if (this.breakoutLoading || !this.mainRoomToken) return;

      this.breakoutLoading = true;

      try {
        const url = this.mainLiveKitUrl;
        const token = this.mainRoomToken;

        // Update reactive refs
        this.roomToken = token;
        this.liveKitUrl = url;
        this.currentBreakoutRoomId = null;

        // connectRoom handles full teardown + event re-binding internally
        await this.connectRoom(url, token);
        this.infoMessage = "Returned to main session.";
      } catch (error) {
        this.errorMessage = error.message || "Failed to return to main room.";
      } finally {
        this.breakoutLoading = false;
      }
    },

    async endBreakoutRooms() {
      if (this.breakoutEndLoading || !this.currentSessionId) return;

      this.breakoutEndLoading = true;
      this.errorMessage = "";

      try {
        await this.$axios.post(
          this.endBreakoutRoomsEndpoint,
          { SessionID: this.currentSessionId, HostUserID: this.currentAdminUserId },
          { headers: { accept: "*/*", "Content-Type": "application/json" } }
        );

        // Broadcast teardown signal to all learners
        const roomToUse = this.liveKitRoom;
        if (roomToUse) {
          try {
            const packet = JSON.stringify({ type: "breakout-ended" });
            roomToUse.localParticipant.publishData(
              new TextEncoder().encode(packet),
              { reliable: true }
            );
          } catch (_) { /* non-fatal */ }
        }

        this.breakoutRooms = [];
        this.isInBreakoutRoom = false;
        this.currentBreakoutRoomId = null;
        this.infoMessage = "All breakout rooms ended. Participants returning to main session.";
        this.showBreakoutDialog = false;

        // If host is currently in a sub-room, return them to main
        if (this.mainRoomToken) {
          await this.returnToMainRoom();
          this.mainRoomToken = "";
          this.mainLiveKitUrl = "";
        }
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to end breakout rooms.";
      } finally {
        this.breakoutEndLoading = false;
      }
    },

    isControlActive(control) {
      if (control.action === "create-poll") {
        return Boolean(this.activePoll || this.showPollDialog);
      }
      if (control.action === "breakout-rooms") {
        return this.isInBreakoutRoom || this.showBreakoutDialog;
      }

      return Boolean(this.controlState[control.action]);
    },

    onDrawStart(e) {
      if (!this.controlState.draw) return;
      const canvas = e.currentTarget;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      this.lastX = e.clientX - rect.left;
      this.lastY = e.clientY - rect.top;
      this.isDrawing = true;
    },

    onDrawMove(e) {
      if (!this.isDrawing || !this.controlState.draw) return;
      const canvas = e.currentTarget;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "#ff3b30";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(this.lastX, this.lastY);
      ctx.lineTo(currentX, currentY);
      ctx.stroke();

      // Broadcast stroke to remote participants via LiveKit data channel
      if (this.liveKitRoom) {
        try {
          const packet = JSON.stringify({
            type: "draw",
            x0: this.lastX / canvas.width,
            y0: this.lastY / canvas.height,
            x1: currentX / canvas.width,
            y1: currentY / canvas.height,
            color: "#ff3b30",
            width: 3,
          });
          this.liveKitRoom.localParticipant.publishData(
            (this.chatEncoder || new TextEncoder()).encode(packet),
            { reliable: false }
          );
        } catch (_) {
          // Non-fatal broadcast error
        }
      }

      this.lastX = currentX;
      this.lastY = currentY;
    },

    onDrawEnd() {
      this.isDrawing = false;
    },

    activateEmbed() {
      const url = this.embedInputDraft.trim();
      if (!url) return;
      this.embedUrl = url;
      this.embedActive = true;
      this.$nextTick(() => {
        const canvas = this.$refs.embedDrawCanvas;
        if (canvas) {
          canvas.width = canvas.offsetWidth;
          canvas.height = canvas.offsetHeight;
        }
      });
    },

    removeEmbed() {
      this.embedActive = false;
      this.embedUrl = "";
      this.embedInputDraft = "";
    },

    addPollOption() {
      if (!this.canAddPollOption) return; // B01 — ไม่เกิน MAX_POLL_OPTIONS (D23)

      this.draftPollOptions.push({
        id: this.nextPollOptionId,
        label: "",
        isCorrect: false,
        imageUrl: "",
        imagePreview: "",
        isUploading: false,
      });
      this.nextPollOptionId += 1;
    },

    removePollOption(optionId) {
      if (this.draftPollOptions.length <= 2) {
        return;
      }

      // B01 — คืนหน่วยความจำของ objectURL รูปตัวเลือกที่ถูกลบ
      const removedOption = this.draftPollOptions.find((option) => option.id === optionId);
      if (removedOption && removedOption.imagePreview) {
        URL.revokeObjectURL(removedOption.imagePreview);
      }

      this.draftPollOptions = this.draftPollOptions.filter((option) => option.id !== optionId);
    },

    resetPollCreator() {
      this.showPollDialog = false;
      this.isPollSubmitting = false;
      this.draftPollQuestion = "";
      this.draftPollType = 1;
      this.answerTimerSeconds = 30;
      this.isAnonymous = false;
      this.showResultsAfterClose = false;
      // B01 — revoke objectURL ทุกตัวก่อนแทนตัวเลือกชุดใหม่
      this.draftPollOptions.forEach((option) => {
        if (option.imagePreview) {
          URL.revokeObjectURL(option.imagePreview);
        }
      });
      this.draftPollOptions = [
        { id: 1, label: "", isCorrect: false, imageUrl: "", imagePreview: "", isUploading: false },
        { id: 2, label: "", isCorrect: false, imageUrl: "", imagePreview: "", isUploading: false },
      ];
      this.nextPollOptionId = 3;
      // B01 — ผลอัปโหลดที่ค้างจาก dialog เก่าจะถูกทิ้ง (D17)
      this.pollDraftVersion += 1;
      this.pollImageTargetId = null;
      this.pollDialogError = "";
    },

    // B01 — เปิด dialog สร้างโพล (ปุ่มในแท็บ Poll และปุ่ม "Create Poll" บนแถบควบคุม)
    openPollDialog() {
      this.pollDialogError = "";
      this.showPollDialog = true;
    },

    // B01 — เปิดตัวเลือกไฟล์สำหรับรูปของตัวเลือกที่ระบุ
    openPollOptionImagePicker(optionId) {
      const input = this.$refs.pollOptionImageInput;
      if (!input) return;

      this.pollImageTargetId = optionId;
      input.value = ""; // ให้เลือกไฟล์เดิมซ้ำได้
      input.click();
    },

    // B01 — ตรวจไฟล์ (D7) แล้วอัปโหลดผ่าน LiveSession/UploadPollOptionImage (D1, D24)
    async handlePollOptionImageSelected(event) {
      const file = event?.target?.files?.[0];
      const targetId = this.pollImageTargetId;
      this.pollImageTargetId = null; // ใช้ target ครั้งเดียว

      if (!file || targetId === null) return;

      const option = this.draftPollOptions.find((item) => item.id === targetId);
      if (!option) return;

      this.pollDialogError = "";

      if (!String(file.type || "").startsWith("image/") || !/\.(gif|jpe?g|png|bmp)$/i.test(file.name || "")) {
        this.pollDialogError = this.$t("livefeature_image_invalid_type");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.pollDialogError = this.$t("livefeature_image_too_large");
        return;
      }

      if (!this.currentSessionId) {
        this.pollDialogError = this.$t("livefeature_live_session_id_missing");
        return;
      }

      const version = this.pollDraftVersion;
      const isCurrent = () => version === this.pollDraftVersion && this.draftPollOptions.includes(option);
      option.isUploading = true;

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("liveSessionId", this.currentSessionId);

        const res = await this.$axios.post(`${this.web_url}LiveSession/UploadPollOptionImage`, formData);
        const url = res.data?.data?.optionImage;

        if (!isCurrent()) return; // D17 — ทิ้งผลของ dialog เก่า / แถวที่ถูกลบ

        if (res.data?.status !== 0 || typeof url !== "string" || !url.startsWith("https://")) {
          this.pollDialogError = this.$t("livefeature_image_upload_failed"); // ไม่ผ่าน D1
          return;
        }

        if (option.imagePreview) {
          URL.revokeObjectURL(option.imagePreview);
        }
        option.imageUrl = url;
        option.imagePreview = URL.createObjectURL(file);
      } catch (error) {
        if (!isCurrent()) return; // error ของ dialog เก่าห้ามโผล่ใน dialog ใหม่

        const status = error.response?.status;
        const reason = error.response?.data?.data?.reason;
        let key = "livefeature_image_upload_failed"; // 401/404/413/500/network/อื่น ๆ (D24)

        if (status === 400 && reason === "INVALID_TYPE") {
          key = "livefeature_image_invalid_type";
        } else if (status === 400 && reason === "TOO_LARGE") {
          key = "livefeature_image_too_large";
        } else if (status === 403) {
          key = "livefeature_image_upload_forbidden";
        }

        this.pollDialogError = this.$t(key);
      } finally {
        option.isUploading = false; // ตั้งบน object ที่จับไว้ (id 1/2 ซ้ำได้หลัง reset)
      }
    },

    // B01 — ลบรูปของตัวเลือก
    removePollOptionImage(optionId) {
      const option = this.draftPollOptions.find((item) => item.id === optionId);
      if (!option) return;

      if (option.imagePreview) {
        URL.revokeObjectURL(option.imagePreview);
      }
      option.imageUrl = "";
      option.imagePreview = "";
      this.pollDialogError = "";
    },

    // B01 — รูปในแผงผลโหลดไม่ได้ → ซ่อนรูป เหลือช่องว่าง
    markPollImageBroken(optionId) {
      this.$set(this.brokenPollImages, optionId, true);
    },

    async handleStartPoll() {
      // B01 — ห้ามเริ่มโพลระหว่างส่ง/อัปโหลดรูป และ error ทั้งหมดแสดงใน dialog (D14)
      if (this.isPollSubmitting || this.isPollImageUploading) return;

      this.pollDialogError = "";

      const trimmedQuestion = this.draftPollQuestion.trim();
      const normalizedOptions = this.draftPollOptions
        .map((option) => option.label.trim())
        .filter(Boolean);

      if (!trimmedQuestion) {
        this.pollDialogError = this.$t("livefeature_poll_question_required");
        return;
      }

      // B01 (D15) — ตัวเลือกที่แนบรูปต้องมีข้อความ
      if (this.draftPollOptions.some((option) => option.imageUrl && !option.label.trim())) {
        this.pollDialogError = this.$t("livefeature_option_image_needs_text");
        return;
      }

      if (normalizedOptions.length < 2) {
        this.pollDialogError = this.$t("livefeature_two_poll_options_required");
        return;
      }

      if (!Number.isInteger(Number(this.answerTimerSeconds)) || Number(this.answerTimerSeconds) <= 0) {
        this.pollDialogError = this.$t("livefeature_valid_answer_timer");
        return;
      }

      if (!this.currentSessionId) {
        this.pollDialogError = this.$t("livefeature_live_session_id_missing");
        return;
      }

      if (!this.currentAdminMemberId) {
        this.pollDialogError = this.$t("livefeature_current_admin_missing");
        return;
      }

      this.isPollSubmitting = true;
      this.errorMessage = "";

      try {
        const pollOptions = this.draftPollOptions
          .filter((option) => option.label.trim())
          .map((option, index) => ({
            id: `draft-${index + 1}`,
            optionText: option.label.trim(),
            optionImage: option.imageUrl || "", // B01 — URI ดิบจาก UploadPollOptionImage
            optionOrder: index,
            isCorrect: Boolean(option.isCorrect),
          }));

        const payload = {
          liveSessionID: this.currentSessionId,
          question: trimmedQuestion,
          pollType: this.draftPollType,
          score: 0,
          answerTimer: parseInt(this.answerTimerSeconds, 10),
          anonymousResponse: this.isAnonymous,
          showResult: this.showResultsAfterClose,
          createdBy: this.currentAdminMemberId,
          options: pollOptions,
        };

        const response = await this.$axios.post(this.addLivePollEndpoint, payload, {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json",
          },
        });

        if (response.data?.status !== undefined && response.data?.status !== 0) {
          throw new Error(response.data?.message || this.$t("livefeature_failed_create_live_poll"));
        }

        this.brokenPollImages = {}; // B01 — โพลเปลี่ยน ล้างสถานะรูปเสีย
        // B01 — meta ของโพลใหม่ที่ AddLivePoll คืน (LivePoll: id, question, createdDate, ...)
        const newPollMeta = {
          ...(response.data?.data || {}),
          question: trimmedQuestion,
        };
        const newPollId = newPollMeta.id || newPollMeta.ID;
        // B01 (D9) — แผงชั่วคราวยังไม่แสดงรูป (URI ดิบเปิดไม่ได้) รูปมากับผลจริงจาก GetPollResults
        this.activePoll = this.buildPollViewModel(
          newPollMeta,
          pollOptions.map((option) => ({ ...option, optionImage: "" }))
        );
        this.showPoll = true;
        this.activeTab = 3;
        this.resetPollCreator();
        // B01 (TC-12) — ดึงผลของโพลใหม่ด้วย id ตรง ๆ (1 request) แทน GetSessionPolls → GetPollResults
        // fetchPollResults จับ error เอง (ไม่ throw) → ไม่ทำให้ pollDialogError ค้าง · ไม่มี id → fallback แบบเดิม
        if (newPollId) {
          await this.fetchPollResults(newPollId, newPollMeta);
        } else {
          await this.fetchSessionPolls();
        }
        this.startPollResultsPolling(); // B01
      } catch (error) {
        // B01 (D14) — ไม่แสดง message จาก API / "Network Error" บนจอ
        console.warn("[Host] handleStartPoll:", error.response?.data?.message || error.message);
        this.pollDialogError = this.$t("livefeature_failed_create_live_poll");
      } finally {
        this.isPollSubmitting = false;
      }
    },

    calculatePercentage(voteCount) {
      const totalVotes = this.totalVotes;

      if (!totalVotes) {
        return 0;
      }

      return Math.round((Number(voteCount || 0) / totalVotes) * 100);
    },

    handleClosePoll() {
      this.showPoll = false;
    },

    async handleEndLive() {
      if (!this.endLiveEndpoint || !this.currentAdminMemberId) {
        this.errorMessage = this.$t("livefeature_unable_end_live_missing");
        return;
      }

      this.isEndingLive = true;
      this.showEndLiveDialog = false;
      this.errorMessage = "";

      try {
        const response = await this.$axios.post(this.endLiveEndpoint, null, {
          params: {
            memberId: this.currentAdminMemberId,
          },
          headers: {
            accept: "*/*",
          },
        });

        if (response.data?.status !== undefined && response.data?.status !== 0) {
          throw new Error(response.data?.message || this.$t("livefeature_failed_end_live"));
        }

        this.sessionMeta.status = "ended";
        this.sessionMeta.statusLabel = this.normalizeStatusLabel("ended");
        const queued = this.hadRecording; // B03 (F3/D27) — จำก่อนตั้ง isRecording = false
        this.isRecording = false;
        this.stopAutoRecordWatch(); // B03
        this.stopRecordingsRefresh(); // B03
        this.disconnectLiveRoom();
        this.clearPersistedLiveTimerStartTime();
        this.stopSessionTimer();
        // Brief pause so the browser releases camera/mic hardware before navigation
        await new Promise((resolve) => setTimeout(resolve, 300));
        // B03 (F3/E36) — replace (Back ไม่กลับหน้า Host ของไลฟ์ที่จบแล้ว) + แจ้งว่าวิดีโอจะเข้าไลบรารี่
        this.$router.replace({
          path: "/home/live",
          query: queued
            ? { refresh: "1", recordingQueued: String(this.currentSessionId) }
            : { refresh: "1" },
        });
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message ||
          error.response?.data?.title ||
          error.message ||
          this.$t("livefeature_failed_end_live");
      } finally {
        this.isEndingLive = false;
      }
    },

    async handleToggleRecording() {
      this.isRecordingLoading = true;
      this.errorMessage = "";

      try {
        if (this.isRecording) {
          // B03 (F1/E17) — sessionId อยู่ใน route · ไม่มี body
          const response = await this.$axios.post(this.stopRecordingEndpoint, null, {
            headers: { accept: "*/*" },
          });

          if (response.data?.status !== undefined && response.data?.status !== 0) {
            throw new Error(response.data?.message || "Failed to stop recording.");
          }

          this.isRecording = false;
          this.hasStoppedRecording = true;
          this.infoMessage = this.$t("livefeature_recording_library_pending"); // B03 (F1)
          this.fetchRecordingLibraryStatus({ silent: true }); // B03 (F4)
        } else {
          this.stopAutoRecordWatch(); // B03 (F1)
          // Poll until LiveKit releases the egress (max 5 attempts × 3 s = 15 s)
          const MAX_ATTEMPTS = 5;
          const RETRY_DELAY_MS = 3000;
          let lastError = null;

          for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            if (attempt > 1) {
              // Show countdown while waiting
              this.isRecordingCooldown = true;
              let remaining = RETRY_DELAY_MS / 1000;
              this.recordingWaitSeconds = remaining;

              await new Promise((resolve) => {
                const tick = setInterval(() => {
                  remaining -= 1;
                  this.recordingWaitSeconds = remaining;
                  if (remaining <= 0) {
                    clearInterval(tick);
                    resolve();
                  }
                }, 1000);
              });

              this.isRecordingCooldown = false;
              this.recordingWaitSeconds = 0;
            }

            try {
              const response = await this.$axios.post(
                this.startRecordingEndpoint,
                {
                  sessionID: this.currentSessionId,
                  memberID: this.currentAdminMemberId,
                  width: this.sessionMeta.recordingWidth || 1280,
                  height: this.sessionMeta.recordingHeight || 720,
                  videoBitrate: 3000000,
                  audioBitrate: 128000,
                  framerate: 30,
                  preset: "H264_720P_30",
                },
                {
                  headers: {
                    accept: "*/*",
                    "Content-Type": "application/json",
                  },
                }
              );

              if (response.data?.status !== undefined && response.data?.status !== 0) {
                const msg = response.data?.message || "";
                const msgLower = msg.toLowerCase();
                // resource_exhausted = egress still terminating, keep retrying
                if (msgLower.includes("resource_exhausted") || msgLower.includes("limit exceeded")) {
                  lastError = new Error(msg);
                  this.infoMessage = `LiveKit still releasing (attempt ${attempt}/${MAX_ATTEMPTS})...`;
                  continue;
                }
                // "already in progress" = recording is already running → treat as success
                if (msgLower.includes("already in progress") || msgLower.includes("already recording")) {
                  // fall through to isRecording = true below
                } else {
                  throw new Error(msg || "Failed to start recording.");
                }
              }

              this.isRecording = true;
              this.infoMessage = "Recording started successfully.";
              lastError = null;
              break;
            } catch (reqErr) {
              const status = reqErr.response?.status;
              const msg = reqErr.response?.data?.message || reqErr.message || "";
              if (
                status === 500 ||
                msg.toLowerCase().includes("resource_exhausted") ||
                msg.toLowerCase().includes("limit exceeded")
              ) {
                lastError = reqErr;
                this.infoMessage = `LiveKit still releasing (attempt ${attempt}/${MAX_ATTEMPTS})...`;
                continue;
              }
              throw reqErr;
            }
          }

          if (lastError) {
            this.infoMessage = "";
            throw new Error("LiveKit could not start a new recording session. Please wait a moment and try again.");
          }
        }
      } catch (error) {
        const backendMessage =
          error.response?.data?.message ||
          error.response?.data?.title ||
          error.message ||
          "";

        if (String(backendMessage).includes("requested room does not exist")) {
          this.errorMessage = "Recording cannot start because the LiveKit room does not exist yet.";
          return;
        }

        this.errorMessage = backendMessage || "Failed to update recording status.";
      } finally {
        this.isRecordingLoading = false;
        this.isRecordingCooldown = false;
        this.recordingWaitSeconds = 0;
      }
    },

    // B02 — Allow to speak / Dismiss เรียก API จริง (D14, D15, D27, D30)
    handleQaApproveSpeaker(question) {
      this.updateQaStatus(question, 1);
    },

    handleQaDismiss(question) {
      this.updateQaStatus(question, 2);
    },

    async updateQaStatus(question, status) {
      if (this.qaActionId || !question || !question.id) return; // D27 — กันกดซ้ำ/สลับคำถามระหว่างรอ

      this.qaActionId = question.id;
      this.qaActionStatus = status;
      this.errorMessage = ""; // D30

      try {
        const res = await this.$axios.post(
          this.updateSessionQAStatusEndpoint,
          { qaID: question.id, status },
          { headers: { accept: "*/*", "Content-Type": "application/json" } }
        );

        if (res.data?.status !== 0) {
          throw new Error(res.data?.message || "UpdateSessionQAStatus failed");
        }

        this.qaSeq++; // D16 — ทิ้งผล poll ที่เริ่มก่อน action สำเร็จ

        if (status === 1) {
          const updated = res.data?.data
            ? this.mapSessionQAItem(res.data.data)
            : { ...question, status: 1, badge: this.$t("livefeature_speaker_allowed") };
          this.questions = this.questions.map((item) => (item.id === question.id ? updated : item));
          this.infoMessage = this.$t("livefeature_qa_allowed_notice");
        } else {
          this.questions = this.questions.filter((item) => item.id !== question.id);
          this.infoMessage = this.$t("livefeature_question_dismissed");
        }
      } catch (error) {
        // D15 — ไม่แสดง message ภาษาอังกฤษจาก API (ห้าม log ข้อความคำถาม)
        console.warn("[Host] updateQaStatus:", error.response?.status, error.response?.data?.message || error.message);
        this.infoMessage = "";
        this.errorMessage = this.$t("livefeature_qa_update_failed");
        this.pollSessionQA({ force: true }); // D30 — รีเฟรชทันทีแม้มี poll ค้าง
      } finally {
        this.qaActionId = null;
        this.qaActionStatus = null;
      }
    },

    // B02 (D11) — poll Q&A ทุก 4 วิ ตลอดที่อยู่หน้า Host
    startQaPolling() {
      this.stopQaPolling();
      this.pollSessionQA();
      this.qaPollInterval = setInterval(() => this.pollSessionQA(), 4000);
    },

    stopQaPolling() {
      if (this.qaPollInterval) {
        clearInterval(this.qaPollInterval);
        this.qaPollInterval = null;
      }
    },

    async pollSessionQA({ force = false } = {}) {
      if (!this.sessionQAEndpoint) return;
      if (this.qaFetching && !force) return; // D11 — ข้ามรอบถ้ายังค้าง

      const seq = ++this.qaSeq; // D16
      this.qaFetching = true;

      try {
        const res = await this.$axios.get(this.sessionQAEndpoint, { headers: { accept: "*/*" } });
        if (seq !== this.qaSeq) return; // ผลเก่า — ทิ้ง

        if (res.data?.status !== 0 || !Array.isArray(res.data?.data)) {
          throw new Error("GetSessionQA failed");
        }

        this.questions = res.data.data
          .filter((item) => item && item.status !== 2)
          .map((item) => this.mapSessionQAItem(item)); // แทนทั้งก้อน · ไม่เลื่อนอัตโนมัติ (D29)
        this.qaLoadedOnce = true;
        this.qaLoadError = false;
      } catch (_) {
        // D28 — ยังไม่เคยโหลดสำเร็จ → error ในแผง (poll ลองต่อ) · เคยสำเร็จแล้ว → เงียบ คงรายการเดิม (รวม 401)
        if (seq === this.qaSeq && !this.qaLoadedOnce) {
          this.qaLoadError = true;
        }
      } finally {
        this.qaFetching = false; // ไม่มีเงื่อนไข — กัน polling ค้างถาวรหลัง action ทำ qaSeq++ (Plan §5 C3)
      }
    },

    mapSessionQAItem(item) {
      const status = Number(item.status);
      return {
        id: item.id,
        question: item.question || "",
        askedBy: item.askedBy || "User",
        time: this.formatDateTimeToTime(item.createdDate),
        status,
        badge: status === 1 ? this.$t("livefeature_speaker_allowed") : this.$t("livefeature_pending"),
      };
    },

    async sendChat() {
      const text = this.chatMessage.trim();
      if (!text) return;

      this.sendingChat = true;
      this.chatMessage = "";

      const replySnapshot = this.replyingToChatID
        ? { name: this.replyingToName, text: this.replyingToText }
        : null;

      const optimisticId = `local-${Date.now()}`;
      this.chatMessages = [
        ...this.chatMessages,
        {
          id: optimisticId,
          sender: "You",
          text,
          time: this.formatDateTimeToTime(Date.now()),
          mine: true,
          replyTo: replySnapshot,
        },
      ];
      this.scrollChatToBottom();

      try {
        if (this.replyingToChatID) {
          const chatID = this.replyingToChatID;
          this.replyingToChatID = null;
          this.replyingToName = null;
          this.replyingToText = null;

          const replyRes = await this.$axios.post(
            this.addSessionChatReplyEndpoint,
            {
              liveSessionChatID: chatID,
              message: text,
              userID: this.currentAdminUserId,
            },
            { headers: { accept: "*/*", "Content-Type": "application/json" } }
          );

          // Remove the optimistic entry and replace with the fully populated
          // server record so the sender name, profileImage and IDs are canonical.
          const serverReply = replyRes.data?.data || {};
          const serverReplyId = serverReply.replyID || serverReply.id || null;
          if (serverReplyId && this.chatSeenIds) this.chatSeenIds.add("reply-" + serverReplyId);
          const confirmedMessage = {
            id: serverReplyId || optimisticId,
            sender: serverReply.fullname || this.currentAdminUserName || "Host",
            text: serverReply.message || text,
            time: this.formatDateTimeToTime(serverReply.commentedTime ? null : Date.now()),
            mine: true,
            replyTo: replySnapshot,
          };

          this.chatMessages = [
            ...this.chatMessages.filter((m) => m.id !== optimisticId),
            confirmedMessage,
          ];
          this.scrollChatToBottom();
        } else {
          // Channel 1: broadcast via LiveKit data channel
          if (this.liveKitRoom) {
            try {
              const packet = JSON.stringify({
                type: "chat",
                id: `rt-${Date.now()}`,
                userID: this.currentAdminUserId,
                name: this.currentAdminUserName || "Host",
                message: text,
                createdAt: Date.now(),
              });
              await this.liveKitRoom.localParticipant.publishData(
                (this.chatEncoder || new TextEncoder()).encode(packet),
                { reliable: true }
              );
            } catch (_) {
              // Non-fatal: real-time broadcast is best-effort
            }
          }

          // Channel 2: persist to API
          const companyId = localStorage.getItem("companyID") || "";
          const res = await this.$axios.post(
            this.addSessionChatEndpoint,
            {
              liveSessionID: this.currentSessionId,
              userID: this.currentAdminUserId,
              companyId,
              message: text,
              senderName: this.currentAdminUserName || "Host",
              sentTime: new Date().toISOString(),
            },
            { headers: { accept: "*/*", "Content-Type": "application/json" } }
          );

          const savedId = res.data?.data?.chatID || res.data?.data?.id || null;
          if (savedId && this.chatSeenIds) this.chatSeenIds.add("chat-" + savedId);
          this.chatMessages = this.chatMessages.map((m) =>
            m.id === optimisticId ? { ...m, id: savedId || optimisticId } : m
          );
        }
      } catch (error) {
        this.chatMessages = this.chatMessages.filter((m) => m.id !== optimisticId);
        this.errorMessage =
          error.response?.data?.message ||
          error.response?.data?.title ||
          error.message ||
          this.$t("livefeature_failed_send_chat");
      } finally {
        this.sendingChat = false;
      }
    },

    /**
     * Normalizes reply/quote data for a single chat item.
     * Returns { replyTo, replyToName, replyToMessage } — spread-ready.
     *
     * Resolution order:
     *   1. Explicit nested replyTo object from API
     *   2. Flat API string fields  (replyToMessage / parentMessage / …)
     *   3. localReplyMap by direct String(id)   ← stable IDs across fetches
     *   4. _pendingReplies queue                ← optimistic-ID→server-ID transition,
     *                                              ONLY for messages new to this fetch
     */
    _normalizeReplyFields(rawChat, id, localReplyMap, prevIdSet) {
      // 1. API returns a structured replyTo object already
      if (rawChat.replyTo && typeof rawChat.replyTo === "object" && rawChat.replyTo.message) {
        const rt = {
          name: String(rawChat.replyTo.name || rawChat.replyTo.userName || rawChat.replyTo.senderName || "User"),
          message: String(rawChat.replyTo.message || rawChat.replyTo.text || ""),
        };
        return { replyTo: rt, replyToName: rt.name, replyToMessage: rt.message };
      }

      // 2. API returns flat reply string fields
      const apiReplyName = String(
        rawChat.replyToUserName || rawChat.parentUserName ||
        rawChat.ReplyToUserName || rawChat.ParentUserName || ""
      ).trim();
      const apiReplyText = String(
        rawChat.replyToMessage || rawChat.parentMessage ||
        rawChat.ReplyToMessage || rawChat.ParentMessage ||
        rawChat.replyText || rawChat.parentText || ""
      ).trim();

      if (apiReplyName || apiReplyText) {
        const rt = { name: apiReplyName || "User", message: apiReplyText };
        return { replyTo: rt, replyToName: rt.name, replyToMessage: rt.message };
      }

      // 3. Direct ID lookup in local snapshot (handles already-stable server IDs)
      const idReply = localReplyMap[id] || null;
      if (idReply) {
        return { replyTo: idReply, replyToName: idReply.name, replyToMessage: idReply.message };
      }

      // 4. Pending send queue — ONLY for messages whose ID was NOT present before
      //    this fetch. This guarantees that old messages (with any text) can never
      //    accidentally consume a queued reply entry.
      const isNewMessage = prevIdSet ? !prevIdSet.has(id) : true;
      if (isNewMessage && this._pendingReplies && this._pendingReplies.length) {
        const serverUserId = String(rawChat.userID ?? rawChat.UserID ?? "");
        const msgText = String(rawChat.message || rawChat.Message || "").trim().toLowerCase();
        const queueIdx = this._pendingReplies.findIndex(
          (p) => p.userId === serverUserId && p.messageText === msgText
        );
        if (queueIdx !== -1) {
          const [matched] = this._pendingReplies.splice(queueIdx, 1);
          return { replyTo: matched.replyTo, replyToName: matched.replyTo.name, replyToMessage: matched.replyTo.message };
        }
      }

      return { replyTo: null, replyToName: "", replyToMessage: "" };
    },

    startReply(msg) {
      this.replyingToChatID = msg.id;
      this.replyingToName = msg.sender;
      this.replyingToText = msg.text;
      this.$nextTick(() => {
        if (this.$refs.chatInput) this.$refs.chatInput.focus();
      });
    },

    startChatPolling() {
      this.pollSessionChat();
      this.chatPollInterval = setInterval(() => {
        this.pollSessionChat();
      }, 4000);
    },

    stopChatPolling() {
      if (this.chatPollInterval) {
        clearInterval(this.chatPollInterval);
        this.chatPollInterval = null;
      }
    },

    // B01 (D10) — refresh ผลโหวตของโพลล่าสุดทุก 3 วินาที
    startPollResultsPolling() {
      // created() เป็น async — ถ้าออกจากหน้าก่อน init เสร็จ ห้ามสร้าง interval บน component ที่ถูกทำลายแล้ว
      if (this._isBeingDestroyed || this._isDestroyed) return;
      if (this.pollResultsInterval) return; // idempotent

      this.pollResultsInterval = setInterval(() => this.refreshActivePollResults(), 3000);
    },

    stopPollResultsPolling() {
      if (this.pollResultsInterval) {
        clearInterval(this.pollResultsInterval);
        this.pollResultsInterval = null;
      }
    },

    async refreshActivePollResults() {
      const pollId = this.activePoll?.id;
      if (!pollId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(pollId))) return;
      if (this.pollResultsInFlight) return;

      this.pollResultsInFlight = true;

      try {
        const res = await this.$axios.get(`${this.web_url}LiveSession/GetPollResults/${pollId}`, {
          headers: {
            accept: "*/*",
          },
        });
        const result = res.data?.data;

        if (!result || String(this.activePoll?.id) !== String(pollId)) return; // โพลเปลี่ยนระหว่างรอ

        const freshById = {};
        (result.options || result.Options || []).forEach((option) => {
          freshById[String(option.optionID || option.OptionID || option.id || option.ID)] = option;
        });

        const options = (this.activePoll.options || []).map((option) => {
          const fresh = freshById[String(option.id)];
          if (!fresh) return option;

          return {
            ...option,
            voteCount: fresh.voteCount || fresh.VoteCount || 0,
            percentage: fresh.percentage || fresh.Percentage || 0,
            // E10 — คง URL รูปเดิม (SAS ใหม่ทุก request จะทำให้รูปโหลดซ้ำ)
            optionImage: option.optionImage || fresh.optionImage || fresh.OptionImage || "",
          };
        });

        // ห้ามแตะ showPoll / infoMessage / errorMessage
        this.activePoll = { ...this.activePoll, options };
      } catch (error) {
        if (error.response?.status === 404) {
          this.stopPollResultsPolling();
        } else {
          console.warn("[Host] refreshActivePollResults:", error);
        }
      } finally {
        this.pollResultsInFlight = false;
      }
    },

    async pollSessionChat() {
      if (!this.currentSessionId) return;
      if (!this.chatSeenIds) this.chatSeenIds = new Set();

      try {
        const res = await this.$axios.get(
          `${this.web_url}LiveSession/GetSessionChat/${this.currentSessionId}`,
          { headers: { accept: "*/*" } }
        );

        const items = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
          ? res.data
          : [];

        const localUserId = parseInt(localStorage.getItem("UserID"), 10);
        const newMsgs = [];

        // Server returns chats newest-first; process oldest-first so the
        // timeline reads top (old) → bottom (new).
        const ordered = [...items].reverse();

        ordered.forEach((item) => {
          const chatId = item.chatID || item.id || item.ID || item.ChatID;
          if (!chatId) return;

          const parentName = item.fullname || item.fullName || item.Fullname || item.senderName || "User";
          const parentText = item.message || item.Message || "";
          const parentTime = item.createdDate || item.CreatedDate || item.sentTime || item.SentTime;

          // ── Top-level message (skip our own — echoed optimistically) ──
          const chatKey = "chat-" + chatId;
          if (!this.chatSeenIds.has(chatKey)) {
            this.chatSeenIds.add(chatKey);
            const isMe = parseInt(item.userID || item.UserID, 10) === localUserId;
            if (!isMe) {
              newMsgs.push({
                id: chatId,
                sender: parentName,
                text: parentText,
                time: this.formatDateTimeToTime(parentTime),
                mine: false,
                replyTo: null,
              });
            }
          }

          // ── Nested replies (quote = the parent message) ──
          const replies = Array.isArray(item.replyList) ? item.replyList
            : Array.isArray(item.ReplyList) ? item.ReplyList : [];
          replies.forEach((rep) => {
            const replyId = rep.replyID || rep.ReplyID || rep.id || rep.ID;
            if (!replyId) return;
            const replyKey = "reply-" + replyId;
            if (this.chatSeenIds.has(replyKey)) return;
            this.chatSeenIds.add(replyKey);

            const repIsMe = parseInt(rep.userID || rep.UserID, 10) === localUserId;
            if (repIsMe) return; // our own replies are echoed optimistically

            const repName = rep.fullname || rep.fullName || rep.Fullname || "User";
            const repText = rep.message || rep.Message || "";
            const repTime = rep.createdDate || rep.CreatedDate || parentTime;
            const quoteName = rep.refReplyFullname || rep.RefReplyFullname || parentName;

            newMsgs.push({
              id: replyKey,
              sender: repName,
              text: repText,
              time: this.formatDateTimeToTime(repTime),
              mine: false,
              replyTo: { name: quoteName, text: parentText },
            });
          });
        });

        if (newMsgs.length) {
          newMsgs.forEach((m) => this.chatMessages.push(m));
          this.scrollChatToBottom();
        }
      } catch (_) {
        // Polling errors are non-fatal
      }
    },

    scrollChatToBottom() {
      this.$nextTick(() => {
        const chatPanel = this.$refs.chatPanel;

        if (!chatPanel) {
          return;
        }

        chatPanel.scrollTop = chatPanel.scrollHeight;
      });
    },

    buildInitials(name) {
      return String(name || "")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("") || "HS";
    },

    formatCurrentTime() {
      return new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatDateTimeToTime(value) {
      if (!value) {
        return this.formatCurrentTime();
      }

      const parsedDate = new Date(value);

      if (Number.isNaN(parsedDate.getTime())) {
        return this.formatCurrentTime();
      }

      return parsedDate.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    normalizeStatus(status) {
      const normalized = String(status || "").toLowerCase();

      if (["live", "2"].includes(normalized)) {
        return "live";
      }

      if (["ended", "completed", "3"].includes(normalized)) {
        return "ended";
      }

      return "scheduled";
    },

    normalizeStatusLabel(status) {
      const normalizedStatus = this.normalizeStatus(status);

      if (normalizedStatus === "live") {
        return this.$t("livefeature_live_badge");
      }

      if (normalizedStatus === "ended") {
        return this.$t("livefeature_ended");
      }

      return this.$t("livefeature_scheduled");
    },

    buildSessionTimeLabel(startTime, endTime) {
      if (!startTime) {
        return this.$t("livefeature_share_prompt");
      }

      const start = new Date(startTime);
      const end = endTime ? new Date(endTime) : null;
      const startLabel = Number.isNaN(start.getTime())
        ? String(startTime)
        : start.toLocaleString([], {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
      const endLabel = end && !Number.isNaN(end.getTime())
        ? end.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "";

      return endLabel ? `${startLabel} - ${endLabel}` : startLabel;
    },

    startSessionTimer(startTime = "") {
      this.stopSessionTimer();

      if (startTime) {
        this.setSessionElapsedFromStart(startTime);
      } else {
        this.sessionElapsedSeconds = 0;
      }

      this.timerIntervalId = window.setInterval(() => {
        this.sessionElapsedSeconds += 1;
      }, 1000);
    },

    stopSessionTimer() {
      if (this.timerIntervalId) {
        window.clearInterval(this.timerIntervalId);
        this.timerIntervalId = null;
      }
    },

    setSessionElapsedFromStart(startTime) {
      const parsedDate = new Date(startTime);

      if (Number.isNaN(parsedDate.getTime())) {
        return;
      }

      const difference = Math.floor((Date.now() - parsedDate.getTime()) / 1000);
      this.sessionElapsedSeconds = Math.max(0, difference);
    },

    resolveLiveTimerStartTime(source = {}) {
      if (!source || typeof source !== "object") {
        return this.getPersistedLiveTimerStartTime();
      }

      const candidates = [
        source.liveStartTime,
        source.LiveStartTime,
        source.actualStartTime,
        source.ActualStartTime,
        source.startedAt,
        source.StartedAt,
        source.startedDate,
        source.StartedDate,
      ].filter(Boolean);

      const validCandidate = candidates.find((value) => !Number.isNaN(new Date(value).getTime()));

      return validCandidate || this.getPersistedLiveTimerStartTime();
    },

    getPersistedLiveTimerStartTime() {
      if (!this.liveTimerStorageKey) {
        return "";
      }

      return localStorage.getItem(this.liveTimerStorageKey) || "";
    },

    persistLiveTimerStartTime(startTime) {
      if (!this.liveTimerStorageKey || !startTime) {
        return;
      }

      localStorage.setItem(this.liveTimerStorageKey, startTime);
    },

    clearPersistedLiveTimerStartTime() {
      if (!this.liveTimerStorageKey) {
        return;
      }

      localStorage.removeItem(this.liveTimerStorageKey);
    },

    buildParticipantStatus(participant) {
      if (participant.joinFlag === true || participant.JoinFlag === true) {
        return {
          label: this.$t("livefeature_status_joined"),
          badgeColor: "#dcfce7",
          badgeText: "#166534",
        };
      }

      if (participant.activeFlag === false || participant.ActiveFlag === false) {
        return {
          label: this.$t("livefeature_inactive"),
          badgeColor: "#fee2e2",
          badgeText: "#b91c1c",
        };
      }

      return {
        label: this.$t("livefeature_registered_label"),
        badgeColor: "#f1f5f9",
        badgeText: "#475569",
      };
    },

    pickParticipantColor(index) {
      const colors = ["#dbeafe", "#dcfce7", "#fde68a", "#fae8ff", "#e2e8f0"];
      return colors[index % colors.length];
    },
  },
};
</script>

<style scoped>
.host-live-page {
  background: #f8fafc;
  min-height: 100vh;
}

.host-header {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
}

.host-header__title {
  color: #0f172a;
  font-size: 1.65rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.host-header__subtitle {
  color: #64748b;
  font-size: 0.92rem;
  margin-top: 6px;
}

.host-header__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-start;
}

.host-header__badge {
  border-radius: 999px;
  font-weight: 700;
}

.host-header__button {
  border-radius: 999px;
  box-shadow: none;
  font-weight: 700;
  letter-spacing: 0;
  min-height: 36px;
  text-transform: none;
}

.host-header__button--danger {
  padding-left: 18px;
  padding-right: 18px;
}

.host-workspace {
  min-height: calc(100vh - 170px);
  width: 100%;
}

.host-workspace__media,
.host-workspace__sidebar {
  display: flex;
  min-width: 0;
}

.stage-column {
  flex: 1;
  width: 100%;
}

.stage-viewport {
  position: relative;
  width: 100%;
}

.stage-canvas {
  align-items: center;
  background: linear-gradient(180deg, #f2f9f6 0%, #eef8f2 100%);
  border: 1px dashed rgba(22, 163, 74, 0.34);
  border-radius: 30px;
  display: flex;
  justify-content: center;
  min-height: 560px;
  overflow: hidden;
  padding: 40px 240px 120px 40px;
  position: relative;
  width: 100%;
}

/* Grid mode: remove the asymmetric padding reserved for the floating camera card */
.stage-canvas--grid {
  align-items: stretch;
  padding: 12px;
  /* Lock the stage to the viewport so the camera grid never forces the
     page to scroll. Leaves room for the dock + bottom control bars. */
  height: 78vh;
  max-height: 78vh;
  min-height: 0;
}

.stage-screenshare {
  bottom: 0;
  height: 100%;
  left: 0;
  object-fit: contain;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 5;
  background: #000;
}

.stage-draw-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  pointer-events: none;
  border-radius: inherit;
}

.stage-draw-canvas--active {
  pointer-events: all;
  cursor: crosshair;
}

.stage-embed-wrap {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 */
  border-radius: 16px;
  overflow: hidden;
  z-index: 6;
}

.stage-embed-frame {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.stage-embed-draw {
  z-index: 11;
  border-radius: 0;
}

.stage-embed-toolbar {
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 70px;
  left: 16px;
  right: 16px;
  z-index: 20;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 12px;
  padding: 6px 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.10);
}

.stage-embed-toolbar__input {
  flex: 1;
}

.stage-canvas::before {
  border: 1px dashed rgba(22, 163, 74, 0.22);
  border-radius: 22px;
  content: "";
  inset: 16px;
  pointer-events: none;
  position: absolute;
}

.stage-canvas__content {
  align-items: center;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
}

.stage-canvas__center {
  position: relative;
  text-align: center;
}

.stage-canvas__icon-wrap {
  align-items: center;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 28px;
  box-shadow: 0 20px 40px rgba(22, 163, 74, 0.12);
  display: inline-flex;
  height: 120px;
  justify-content: center;
  width: 120px;
}

.stage-canvas__headline {
  color: #0f172a;
  font-size: 1.4rem;
  font-weight: 800;
  line-height: 1.35;
  margin-top: 22px;
  min-height: 76px;
}

.stage-canvas__subline {
  color: #64748b;
  font-size: 0.95rem;
  margin-top: 8px;
}

.stage-canvas__watching {
  align-items: center;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 999px;
  color: #475569;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 8px 14px;
  position: absolute;
  right: 24px;
  top: 24px;
  z-index: 10;
}

.stage-canvas__rec-badge {
  align-items: center;
  background: rgba(220, 38, 38, 0.88);
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 800;
  gap: 6px;
  left: 24px;
  letter-spacing: 0.08em;
  padding: 6px 12px;
  position: absolute;
  top: 24px;
  z-index: 10;
}

.stage-canvas__rec-dot {
  animation: rec-pulse 1.2s ease-in-out infinite;
  background: #fff;
  border-radius: 50%;
  display: inline-block;
  height: 8px;
  width: 8px;
}

@keyframes rec-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
}

.host-header__rec-status {
  align-items: center;
  color: #dc2626;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  gap: 6px;
  letter-spacing: 0.04em;
}

.host-header__rec-dot {
  animation: rec-pulse 1.2s ease-in-out infinite;
  background: #dc2626;
  border-radius: 50%;
  display: inline-block;
  height: 8px;
  width: 8px;
}

/* B03 (F2/D24) — ป้ายสถานะ auto-record ข้างปุ่มอัด (ต้องอยู่หลัง .host-header__rec-status) */
.host-header__auto-record {
  align-items: flex-start;
  letter-spacing: 0;
  line-height: 1.35;
  max-width: 360px;
  white-space: normal;
}

.host-header__auto-record--starting {
  color: #64748b;
}

.host-header__auto-record--not_started {
  color: #92400e;
}

.stage-canvas__timer {
  align-items: center;
  background: rgba(0, 0, 0, 0.6) !important;
  border-radius: 999px;
  color: #ffffff !important;
  display: inline-flex;
  font-size: 0.84rem;
  font-weight: 700;
  left: 24px;
  padding: 8px 14px;
  position: absolute;
  bottom: 24px;
  z-index: 10;
}

/* ================================================================
   Conference grid — Google Meet style video stage
   ================================================================ */

/* Grid mode: tiles fill the full stage as auto-fit columns */
.conf-grid {
  /* Default focal sizing; overridden inline by `gridStyleVars` per count. */
  --card-min-width: 45%;
  align-content: center;
  display: grid;
  gap: 16px;
  grid-auto-rows: 1fr;
  grid-template-columns: repeat(auto-fit, minmax(var(--card-min-width), 1fr));
  height: 100%;
  justify-content: center;
  min-height: 0;
  overflow: hidden;
  position: relative;
  width: 100%;
  z-index: 6;
}

/* Filmstrip mode: absolute horizontal strip at bottom when screen sharing */
.conf-grid--filmstrip {
  bottom: 10px;
  display: flex;
  flex-direction: row;
  gap: 10px;
  height: auto;
  left: 10px;
  min-height: 0;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  position: absolute;
  right: 10px;
  z-index: 8;
}

.conf-tile {
  align-self: stretch;
  background: #0f172a;
  border-radius: 14px;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  position: relative;
}

/* Host tile: subtle green accent border */
.conf-tile--self {
  border: 2px solid rgba(22, 163, 74, 0.5);
}

/* Filmstrip tiles: fixed size, drop aspect-ratio constraint */
.conf-grid--filmstrip .conf-tile {
  aspect-ratio: unset;
  flex-shrink: 0;
  height: 108px;
  width: 160px;
}

.conf-tile__video {
  border-radius: inherit;
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.conf-tile__avatar {
  align-items: center;
  background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.conf-tile__initials {
  color: #1d4ed8;
  font-size: 1.2rem;
  font-weight: 800;
}

.conf-tile__bar {
  align-items: center;
  background: linear-gradient(to top, rgba(15, 23, 42, 0.75) 0%, transparent 100%);
  bottom: 0;
  color: #ffffff;
  display: flex;
  font-size: 0.78rem;
  font-weight: 600;
  gap: 6px;
  justify-content: space-between;
  left: 0;
  padding: 8px 10px 4px;
  position: absolute;
  right: 0;
}

.conf-tile__name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remote-audio-sink {
  height: 0;
  left: -9999px;
  overflow: hidden;
  position: absolute;
  top: -9999px;
  width: 0;
}

.breakout-dialog__header {
  align-items: center;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-bottom: 1px solid rgba(22, 163, 74, 0.12);
  display: flex;
  padding: 18px 20px 14px;
}

.breakout-dialog__title {
  color: #14532d;
  font-size: 1rem;
  font-weight: 700;
}

.breakout-dialog__field-label {
  color: #334155;
  font-size: 0.875rem;
  font-weight: 600;
}

.breakout-dialog__hint {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  color: #166534;
  font-size: 0.82rem;
  padding: 10px 14px;
}

.breakout-room-row {
  align-items: center;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  padding: 10px 14px;
}

.breakout-room-row__info {
  align-items: center;
  display: flex;
}

.breakout-room-row__name {
  color: #0f172a;
  font-size: 0.875rem;
  font-weight: 600;
}

/* Manual Assignment UI */
.breakout-toggle {
  background: #f1f5f9 !important;
  border-radius: 8px;
}

.manual-assignment__hint {
  color: #64748b;
  font-size: 0.82rem;
}

.manual-assignment__grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  max-height: 400px;
  overflow-y: auto;
}

.room-column {
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  min-height: 150px;
}

.room-column__header {
  align-items: center;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-bottom: 1px solid #bbf7d0;
  border-radius: 10px 10px 0 0;
  color: #166534;
  display: flex;
  font-size: 0.875rem;
  font-weight: 700;
  padding: 10px 12px;
}

.room-column__participants {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
}

.participant-chip {
  align-items: center;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  gap: 8px;
  padding: 6px 8px;
  transition: all 0.2s;
}

.participant-chip:hover {
  border-color: #16a34a;
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.1);
}

.participant-chip--unassigned {
  background: #fef3c7;
  border-color: #fcd34d;
}

.participant-chip__avatar {
  align-items: center;
  background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
  border-radius: 50%;
  color: white;
  display: flex;
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 700;
  height: 28px;
  justify-content: center;
  width: 28px;
}

.participant-chip--unassigned .participant-chip__avatar {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
}

.participant-chip__name {
  color: #0f172a;
  flex: 1;
  font-size: 0.8rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.unassigned-pool {
  background: #fffbeb;
  border: 2px dashed #fcd34d;
  border-radius: 12px;
  padding: 12px;
}

.unassigned-pool__header {
  align-items: center;
  color: #92400e;
  display: flex;
  font-size: 0.875rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.unassigned-pool__participants {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stage-dock {
  align-items: center;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 999px;
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  justify-content: flex-start;
  min-width: auto !important;
  margin-top: 28px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 7px 9px;
  position: relative;
  width: min(100%, 920px) !important;
  max-width: min(100%, 920px);
  flex-grow: 0 !important;
  z-index: 30;
}

.stage-dock--between {
  margin: 18px auto 0;
}

.stage-dock__action {
  align-items: center;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 999px;
  color: #0f172a;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  gap: 6px;
  min-height: 34px;
  min-width: 98px;
  padding: 0 16px;
  white-space: nowrap;
}

.stage-dock__action >>> .v-icon {
  color: #0f172a !important;
}

.stage-dock__icon--loading {
  animation: stage-dock-spin 1s linear infinite;
}

@keyframes stage-dock-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.stage-dock__action--active {
  background: rgb(22, 163, 74);
  border-color: rgb(22, 163, 74);
  color: #ffffff;
}

.stage-dock__action--active >>> .v-icon {
  color: #ffffff !important;
}

.stage-dock__action--poll {
  background: rgba(22, 163, 74, 0.08);
  border-color: rgba(22, 163, 74, 0.24);
  color: #166534;
}

.stage-dock__action--poll >>> .v-icon {
  color: #16a34a !important;
}

.poll-panel {
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 26px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
  margin-top: 24px;
  padding: 28px 28px 24px;
  width: 100%;
}

.poll-panel__header {
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
  padding-bottom: 20px;
}

.poll-panel__title-row {
  align-items: center;
  display: flex;
  gap: 12px;
}

.poll-panel__live-badge {
  align-items: center;
  background: #fef2f2;
  border-radius: 999px;
  color: #dc2626;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  padding: 6px 12px;
  text-transform: uppercase;
}

.poll-panel__title {
  color: #475569;
  font-size: 0.92rem;
  font-weight: 700;
}

.poll-panel__question {
  color: #0f172a;
  font-size: 1.22rem;
  font-weight: 800;
  line-height: 1.45;
}

.poll-panel__item-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.poll-panel__option-label {
  color: #1e293b;
  font-size: 0.95rem;
  font-weight: 700;
  min-width: 0;
}

.poll-panel__option-metrics {
  color: #64748b;
  flex-shrink: 0;
  font-size: 0.92rem;
  font-weight: 700;
  white-space: nowrap;
}

.poll-panel__track {
  background: #e5e7eb;
  border-radius: 999px;
  height: 8px;
  overflow: hidden;
}

.poll-panel__fill {
  background: linear-gradient(90deg, #93c5fd 0%, #60a5fa 100%);
  border-radius: 999px;
  height: 100%;
  transition: width 0.3s ease;
}

.poll-panel__fill--primary {
  background: linear-gradient(90deg, #2563eb 0%, #3b82f6 100%);
}

/* B01 — ช่องรูปตัวเลือกในแผงผลโหวต (40×40) */
.poll-panel__thumb-slot {
  background: #f1f5f9;
  border-radius: 8px;
  display: inline-flex;
  flex-shrink: 0;
  height: 40px;
  overflow: hidden;
  width: 40px;
}

.poll-panel__option-thumb {
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.poll-panel__option-text {
  min-width: 0;
  overflow-wrap: anywhere;
}

.poll-panel__footer {
  align-items: center;
  border-top: 1px solid rgba(226, 232, 240, 0.9);
  display: flex;
  font-size: 0.9rem;
  font-weight: 700;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 18px;
}

.poll-panel__total-votes {
  color: #475569;
}

.poll-panel__close-primary {
  align-items: center;
  background: #2563eb;
  border: none;
  border-radius: 999px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 700;
  justify-content: center;
  min-height: 40px;
  padding: 0 20px;
  transition: background 0.2s ease;
}

.poll-panel__close-primary:hover {
  background: #1d4ed8;
}

.poll-dialog {
  border-radius: 28px !important;
  overflow: hidden;
}

.poll-dialog__header {
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  padding: 20px 24px;
}

.poll-dialog__header-left {
  align-items: center;
  display: flex;
}

.poll-dialog__title {
  color: #0f172a;
  font-size: 1.15rem;
  font-weight: 800;
}

.poll-dialog__body {
  padding: 24px !important;
}

.poll-dialog__label {
  color: #0f172a;
  display: inline-block;
  font-size: 0.88rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.poll-dialog__required {
  color: #ef4444;
  font-weight: 700;
}

.poll-dialog__options-hint-inline {
  color: #94a3b8;
  font-size: 0.82rem;
  font-weight: 400;
  text-transform: none;
}

.poll-dialog__answer-type {
  display: flex;
  gap: 12px;
}

.poll-dialog__type-chip {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  color: #334155;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.88rem;
  font-weight: 600;
  gap: 6px;
  padding: 8px 18px;
  transition: all 0.15s ease;
}

.poll-dialog__type-chip:hover {
  border-color: #16a34a;
}

.poll-dialog__type-chip--active {
  background: #16a34a;
  border-color: #16a34a;
  color: #ffffff;
}

.poll-dialog__type-code {
  color: inherit;
  font-size: 0.8rem;
  font-weight: 400;
  opacity: 0.8;
}

.poll-dialog__option-row {
  align-items: center;
  display: flex;
  gap: 10px;
}

.poll-dialog__option-num {
  align-items: center;
  background: #dcfce7;
  border-radius: 50%;
  color: #16a34a;
  display: flex;
  flex-shrink: 0;
  font-size: 0.82rem;
  font-weight: 700;
  height: 28px;
  justify-content: center;
  width: 28px;
}

.poll-dialog__option-input {
  flex: 1;
}

.poll-dialog__input >>> .v-input__slot,
.poll-dialog__option-input >>> .v-input__slot {
  border-radius: 8px !important;
}

/* B01 — ช่องรูปของตัวเลือกใน dialog (กว้างคงที่ 64px) */
.poll-dialog__option-image {
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 4px;
  justify-content: center;
  width: 64px;
}

.poll-dialog__option-thumb {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  height: 40px;
  object-fit: cover;
  width: 40px;
}

.poll-dialog__add-option {
  align-items: center;
  background: transparent;
  border: 2px dashed #c8e6c9;
  border-radius: 10px;
  color: #16a34a;
  cursor: pointer;
  display: flex;
  font-size: 0.9rem;
  font-weight: 600;
  justify-content: center;
  margin-top: 8px;
  padding: 12px;
  transition: background 0.15s ease;
  width: 100%;
}

.poll-dialog__add-option:hover {
  background: #f0fdf4;
}

/* B01 — ครบ MAX_POLL_OPTIONS ตัวเลือก */
.poll-dialog__add-option:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.poll-dialog__add-option:disabled:hover {
  background: transparent;
}

.poll-dialog__options-limit {
  color: #94a3b8;
  font-size: 0.8rem;
  margin-top: 6px;
  text-align: center;
}

.poll-dialog__settings-section {
  border-top: 1px solid #f1f5f9;
  padding-top: 16px;
}

.poll-dialog__settings-header {
  align-items: center;
  display: flex;
}

.poll-dialog__settings-title {
  color: #16a34a;
  font-size: 0.9rem;
  font-weight: 700;
}

.poll-dialog__setting-row {
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  padding: 14px 0;
}

.poll-dialog__setting-row--last {
  border-bottom: none;
}

.poll-dialog__setting-info {
  flex: 1;
  padding-right: 16px;
}

.poll-dialog__setting-name {
  color: #1e293b;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
}

.poll-dialog__setting-desc {
  color: #94a3b8;
  font-size: 0.8rem;
  margin: 2px 0 0;
}

.poll-dialog__setting-control {
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 8px;
}

.poll-dialog__timer-input {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 6px 8px;
  text-align: center;
  width: 60px;
}

.poll-dialog__timer-unit {
  color: #64748b;
  font-size: 0.82rem;
}

/* B01 — alert ของ dialog อยู่นอกส่วนที่ scroll (D14) */
.poll-dialog__error {
  flex: 0 0 auto;
  padding: 12px 24px 0;
}

.poll-dialog__actions {
  border-top: 1px solid #f1f5f9;
  padding: 16px 24px !important;
}

.poll-dialog__actions .v-btn {
  border-radius: 8px;
  letter-spacing: 0;
  text-transform: none;
}

.sidebar-shell {
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 30px;
  display: flex;
  flex: 1;
  flex-direction: column;
  height: calc(100vh - 240px);
  max-height: calc(100vh - 240px);
  min-height: 0;
  min-height: calc(100vh - 240px);
  overflow: hidden;
}

.sidebar-tabs {
  border-bottom: 1px solid rgba(148, 163, 184, 0.14);
  flex: 0 0 auto;
}

.sidebar-tabs__tab {
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0;
  min-width: auto;
  text-transform: none;
}

.sidebar-tabs-items {
  background: #ffffff;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sidebar-tabs-items >>> .v-window__container,
.sidebar-tabs-items >>> .v-window-item {
  height: 100%;
  min-height: 0;
}

.sidebar-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
}

.sidebar-panel--chat {
  overflow: hidden;
}

.chat-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

/* ── Chat Row Layout ───────────────────────────────── */
.chat-row {
  align-items: flex-end;
  display: flex;
  gap: 8px;
}

.chat-row--mine {
  flex-direction: row-reverse;
}

.chat-row--theirs {
  flex-direction: row;
}

.chat-row--pending {
  opacity: 0.6;
}

/* ── Avatar ────────────────────────────────────────── */
.chat-row__avatar {
  margin-bottom: 22px;
}

.chat-row__initials {
  color: #0f172a;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}

/* ── Body ──────────────────────────────────────────── */
.chat-row__body {
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  gap: 2px;
  max-width: 72%;
  min-width: 0;
}

.chat-row--mine .chat-row__body {
  align-items: flex-end;
}

.chat-row--theirs .chat-row__body {
  align-items: flex-start;
}

/* ── Sender name ───────────────────────────────────── */
.chat-row__sender {
  color: #475569;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  margin-bottom: 1px;
  padding-left: 2px;
}

/* ── Bubble + reply button group ───────────────────── */
.chat-row__group {
  align-items: flex-end;
  display: flex;
  gap: 4px;
}

.chat-row--mine .chat-row__group {
  flex-direction: row-reverse;
}

.chat-row__reply-btn {
  align-self: center;
  color: #94a3b8 !important;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.18s ease, color 0.18s ease;
}

.chat-row:hover .chat-row__reply-btn {
  opacity: 1;
}

.chat-row__reply-btn:hover {
  color: #16a34a !important;
}

/* ── Bubble ────────────────────────────────────────── */
.chat-row__bubble {
  font-size: 0.875rem;
  line-height: 1.55;
  padding: 9px 13px;
  word-break: break-word;
}

.chat-row__bubble--mine {
  background: #a6cc39;
  border-radius: 16px 16px 4px 16px;
  box-shadow: 0 2px 8px rgba(166, 204, 57, 0.22);
  color: #ffffff;
}

.chat-row__bubble--theirs {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px 16px 16px 4px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
  color: #1e293b;
}

/* ── Reply context banner (inside bubble) ──────────── */
.reply-context-banner {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-bottom: 6px;
  overflow: hidden;
  padding: 4px 8px;
}

.reply-context-banner--mine {
  background: rgba(0, 0, 0, 0.15);
  border-left: 3px solid #2ecc71;
}

.reply-context-banner--theirs {
  background: rgba(0, 0, 0, 0.04);
  border-left: 3px solid #95a5a6;
}

.reply-user-name {
  color: #2ecc71;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.reply-context-banner--mine .reply-user-name {
  color: rgba(255, 255, 255, 0.92);
}

.reply-context-banner--theirs .reply-user-name {
  color: #2ecc71;
}

.reply-text-preview {
  color: #94a3b8;
  font-size: 0.8rem;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reply-context-banner--mine .reply-text-preview {
  color: rgba(255, 255, 255, 0.65);
}

.reply-context-banner--theirs .reply-text-preview {
  color: #64748b;
}

/* ── Timestamp & delivery status ───────────────────── */
.chat-row__foot {
  align-items: center;
  display: flex;
  gap: 3px;
  padding: 0 2px;
}

.chat-row--mine .chat-row__foot {
  justify-content: flex-end;
}

.chat-row--theirs .chat-row__foot {
  justify-content: flex-start;
}

.chat-row__ts {
  color: #94a3b8;
  font-size: 0.68rem;
  letter-spacing: 0.02em;
}

.chat-row__status-icon--pending {
  color: #94a3b8 !important;
}

.chat-row__status-icon--sent {
  color: #22c55e !important;
}

/* ── Participant & shared rules ────────────────────── */
.participant-row__name {
  color: #0f172a;
  font-size: 0.92rem;
  font-weight: 800;
}

.chat-row__role,
.participant-row__role,
.qa-panel__meta {
  color: #64748b;
  font-size: 0.8rem;
}

.chat-row__role {
  margin-top: 2px;
}

/* ── Reply banner (compose area) ───────────────────── */
/* ── Reply compose banner ──────────────────────────── */
.chat-reply-banner {
  align-items: center;
  background: #f0faf6;
  border-left: 3px solid #6CC8A0;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  padding: 6px 10px;
}

.chat-reply-banner__content {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
}

.chat-reply-banner__name {
  color: #2d9e72;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

/* ── Quote block inside bubble ─────────────────────── */
.chat-quote {
  border-left: 3px solid #6CC8A0;
  border-radius: 6px 6px 0 0;
  background: rgba(108, 200, 160, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-bottom: 4px;
  padding: 4px 8px;
}

.chat-quote--mine {
  background: rgba(255, 255, 255, 0.18);
  border-left-color: rgba(255, 255, 255, 0.7);
}

.chat-quote-name {
  color: #6CC8A0;
  font-size: 11px;
  font-weight: 700;
}

.chat-quote--mine .chat-quote-name {
  color: rgba(255, 255, 255, 0.9);
}

.chat-quote-text {
  color: #475569;
  font-size: 12px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-quote--mine .chat-quote-text {
  color: rgba(255, 255, 255, 0.7);
}

/* ── Per-bubble reply button ───────────────────────── */
.chat-reply-btn {
  font-size: 0.72rem !important;
  min-width: 0 !important;
  opacity: 0;
  padding: 0 4px !important;
  transition: opacity 0.18s ease;
}

.chat-bubble-wrap:hover .chat-reply-btn {
  opacity: 1;
}

.chat-compose {
  flex: 0 0 auto;
  padding-top: 8px;
}

.chat-input-pill {
  border-radius: 30px !important;
}

.chat-input-pill >>> .v-input__control,
.chat-input-pill >>> .v-input__slot,
.chat-input-pill >>> fieldset {
  border-radius: 9999px !important;
}

.chat-input-pill >>> .v-input__slot {
  align-items: center !important;
  background: #ffffff !important;
  border: 1px solid #cbd5e1 !important;
  box-shadow: none !important;
  min-height: 48px !important;
  padding-left: 20px !important;
  padding-right: 6px !important;
}

.chat-input-pill >>> input {
  font-size: 0.9rem;
}

.chat-input-pill >>> .v-input__append-inner {
  align-self: center;
  margin-top: 0 !important;
}

.chat-input-pill >>> .v-input__icon--append .v-icon {
  background-color: #2ecc71;
  border-radius: 50%;
  color: #ffffff !important;
  cursor: pointer;
  font-size: 16px !important;
  height: 32px !important;
  line-height: 32px;
  text-align: center;
  width: 32px !important;
}

.participant-row {
  align-items: center;
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 18px;
  display: flex;
  justify-content: space-between;
  padding: 12px;
}

.participant-row__left {
  align-items: center;
  display: flex;
  min-width: 0;
}

/* B03 (F4) — แถวการบันทึก (ต้องอยู่หลัง .participant-row — specificity เท่ากัน) */
.host-rec-row {
  align-items: stretch;
  flex-direction: column;
  gap: 6px;
}

.host-rec-row__meta {
  overflow-wrap: anywhere;
}

.host-rec-reload {
  letter-spacing: 0;
  text-transform: none;
}

/* B03 (F3) — !important: Vuetify .theme--light.v-card > .v-card__text ชนะ scoped class */
.host-end-dialog__note {
  color: #475569 !important;
  font-size: 0.9rem;
}

.qa-panel {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 22px;
}

.qa-panel__eyebrow {
  color: #16a34a;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

/* B02 (D26) — ป้าย Pending สีต่างจาก Speaker allowed */
.qa-panel__eyebrow--pending {
  color: #854d0e;
}

.qa-panel__question {
  color: #0f172a;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.45;
  overflow-wrap: anywhere; /* B02 (D19) */
  white-space: pre-wrap; /* B02 (D19) */
}

/* B02 (D27) — ปุ่ม Allow/Dismiss ตัดบรรทัดในปุ่มแทนการดันแผงให้เลื่อนแนวนอน */
.qa-panel__action {
  height: auto !important;
  letter-spacing: 0;
  max-width: 100%;
  min-height: 36px;
  text-transform: none;
  white-space: normal;
}

.qa-panel__action >>> .v-btn__content {
  flex: 1 1 auto; /* B02 — Vuetify ตั้ง flex: 1 0 auto ทำให้ข้อความไม่ตัดบรรทัด (Plan §5 C1) */
  max-width: 100%;
  min-width: 0;
  white-space: normal;
}

@media (max-width: 1264px) {
  .stage-dock {
    flex-wrap: wrap;
    justify-content: center;
    max-width: 100%;
    overflow-x: visible;
  }
}

@media (max-width: 960px) {
  .poll-panel__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .host-workspace {
    min-height: auto;
  }

  .stage-canvas,
  .sidebar-shell {
    height: auto;
    max-height: none;
    min-height: 620px;
  }
}

@media (max-width: 600px) {
  .host-live-page {
    padding: 12px;
  }

  .host-header__title {
    font-size: 1.28rem;
  }

  .stage-canvas {
    min-height: 520px;
    padding: 12px;
  }

  .stage-canvas__watching {
    left: 20px;
    right: auto;
    top: 20px;
  }

  .conf-grid--filmstrip {
    left: 8px;
    right: 8px;
  }

  .stage-dock {
    max-width: 100%;
  }

  .stage-dock__action {
    justify-content: center;
    min-width: calc(50% - 8px);
  }

  .poll-panel {
    padding: 20px 18px 18px;
  }

  .poll-panel__item-head,
  .chat-row__meta,
  .participant-row {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>